// Support at Home Suggestion Engine Panel – strategy comparison and optimisation for Support at Home funding.
// Renamed from SupportAtHomeSuggestionEngine for clarity (parallel to AgedCareSuggestionEnginePanel).

import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { calculateAge } from '../../utils/index.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import type { GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import { sahAnnuityInvestable } from '../../strategy/SupportAtHomeStrategyGenerator.ts';
import { sahPlanAnnuityOf, sahAnnuityPersonName, planAnnuityIncomeOf, SAH_COMPARE_NOTE } from './sahGuaranteedIncome.ts';
import { SameAsBestEstateNote } from '../../components/strategy/SameAsBestEstateNote.tsx';
import { pickCashflowCoveredLeader, cashflowCoveredNote, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, GOAL_CHIP_LABELS, friendlyGoalLabel, coveredIsBestEstate, SAME_PLAN_NOTE } from '../../strategy/cashflowCoveredGoal.ts';
import { AnnuityExplainerModal, EngineAnnuityTerms } from '../../components/strategy/AnnuityTerms.tsx';
import { EquityReleaseTerms } from '../../components/strategy/EquityReleaseTerms.tsx';
import { SAH_STRATEGY_EXPLAINERS } from './sahStrategyExplainers.ts';
import { cashflowNotOnTiles, otherCashflowLines } from './sahCashflowLines.ts';
import { allocationName, assetStatusWords } from './sahAssetWords.ts';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { JargonTerm } from '../../components/narrative/index.tsx';
import {
    BrainCircuit, Settings, CheckCircle,
    Target, Lightbulb, AlertCircle,
    Eye, X, HelpCircle, ChevronDown, ChevronUp,
    Save, Home, Info, ShieldCheck, RefreshCw, ClipboardList
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';

interface TooltipRow {
    label?: string;
    value?: number | string;
    [key: string]: unknown;
}

interface CalculationTooltipProps {
    title: React.ReactNode;
    data?: TooltipRow[];
    children: React.ReactNode;
}

// Calculation Tooltip Component (matching Aged Care)
const CalculationTooltip = ({ title, data, children }: CalculationTooltipProps) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const tooltipHeight = tooltipRef.current.offsetHeight || 180;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let newPosition: 'top' | 'bottom' = position;
            if (spaceBelow < tooltipHeight + 16 && spaceAbove > tooltipHeight + 16) {
                newPosition = 'top';
            } else {
                newPosition = 'bottom';
            }
            setPosition(newPosition);
            setCoords({
                top: newPosition === 'top' ? rect.top - tooltipHeight - 16 : rect.bottom + 16,
                left: rect.left + rect.width / 2
            });
        }
    }, [show, position]);

    if (!Array.isArray(data)) return <>{children}</>;

    return (
        <div
            className="relative"
            ref={wrapperRef}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: 'translateX(-50%)',
                            zIndex: 10001,
                            width: '340px',
                            maxWidth: '90vw',
                            pointerEvents: 'auto'
                        }}
                        className="p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
                    >
                        <h4 className="font-bold text-sm mb-2 text-blue-300">{title}</h4>
                        <div className="space-y-1">
                            {data.map((item, index) => {
                                let displayValue: string | number | undefined = item.value;
                                if (typeof displayValue === 'number' && !isNaN(displayValue)) {
                                    displayValue = Math.round(displayValue).toLocaleString();
                                }
                                return item.label?.startsWith('---') ? (
                                    <hr key={`hr-${item.label}-${index}`} className="border-gray-600 my-1" />
                                ) : (
                                    <div key={`row-${item.label}-${index}`} className="flex justify-between">
                                        <span className="text-gray-400">{item.label}:</span>
                                        <span className="font-mono font-semibold">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

import { getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { calculateServicePlanCosts as calculateServiceCostsUtil, servicePlanBudgetsOf } from '../../../ServicePlanUtils.ts';
import { analyzeBaselineOpportunities, identifyIrrelevantStrategies } from '../../strategy/SupportAtHomeProblemIdentifier.ts';
import { generateAllStrategies } from '../../strategy/SupportAtHomeStrategyGenerator.ts';
import { rankStrategies } from '../../strategy/SupportAtHomeStrategyEvaluator.ts';
import { applyRadFundingToFactFind, extractAgedCareIntegrationDetails } from '../../strategy/StrategyIntegrationUtils.ts';
import {
    buildSupportAtHomeAssumptions,
    flattenSupportAtHomeAssumptionsForModal,
} from '../../strategy/supportAtHomeStrategyAssumptions.ts';
import { resolveServicePlanForSuggestionEngine } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import {
    buildRecommendedOptimalStrategies,
    enrichStrategiesWithRecommendationLabels,
    goalLeaderFor,
} from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { decidableClientLiabilities } from '../../strategy/supportAtHomeStrategyFunding.ts';
import SupportAtHomeSuggestionEngineDeepDive from './SupportAtHomeSuggestionEngineDeepDive.tsx';
import type { SupportAtHomeSuggestionEngineDeepDiveProps } from './SupportAtHomeSuggestionEngineDeepDive.tsx';
import {
    buildHomeCareStrategyFromSuggestionEngine,
    persistHomeCareStrategy,
    getSavedHomeCareStrategies,
    markChosenHomeCarePlanOutOfDate,
    getAllSavedStrategies,
} from './homeCareStrategyStorage.tsx';
import { sahChosenOutOfDate, sahHouseholdFingerprint, sahHouseholdOf } from './sahChosenStale.ts';
import { NOT_FOUND_ON_RETEST, outOfDateOf } from '../../utils/chosenPlanRefresh.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import {
    buildSupportAtHomeDigitalSOASnapshot,
    openSupportAtHomeDigitalSOAAttachment,
} from './SupportAtHomeDigitalSOAAttachment.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { hasAnsweredNewMoneyProfile, INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import { CurrencyInput } from './SupportAtHomeHelpers.tsx';
import { StructureFundingInSuggestionsFields } from '../agedCare/StructureFundingInSuggestionsFields.tsx';
import type { StructureFundingInSuggestions } from '../agedCare/StructureFundingInSuggestionsFields.tsx';
import { Button } from '../../components/ui/index.tsx';
import { ShieldAlert } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromSupportAtHomeType } from '../../strategy/strategyRisks.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindGoals, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy, AgedCareIntegrationDetails } from '../../strategy/StrategyIntegrationUtils.ts';
import type { ProblemStatement, FilteredStrategiesResult } from '../../strategy/SupportAtHomeProblemIdentifier.ts';
import type { GoalPriority, BaselineAnalysis } from '../../strategy/SupportAtHomeStrategyEvaluator.ts';
import type {
    EvaluatedStrategy,
    StrategySnapshot,
    RecommendedStrategyEntry,
} from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import type {
    SupportAtHomeStrategySettings,
    SupportAtHomeAssumptions,
} from '../../strategy/supportAtHomeStrategyAssumptions.ts';
import type { DownsizePropertySettings } from '../../strategy/supportAtHomeDownsizeScenarioUtils.ts';

/** This panel always initialises `propertyStrategies` (unlike SupportAtHomeStrategySettings's optional
 * field, which some other callers omit), so the settings-modal JSX below can read it without `?.`. */
type SahStrategySettings = SupportAtHomeStrategySettings & { propertyStrategies: DownsizePropertySettings };
import type { SupportAtHomeServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import type { SupportAtHomeContributions, SupportAtHomeProjectionYear } from '../../strategy/supportAtHomeProjectionUtils.ts';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import DialogShell from '../../components/ui/DialogShell.tsx';
import ComparisonTable, { type ComparisonRow } from '../../components/strategy/ComparisonTable.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import type { GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import SahPlansView, { sahMovedAmount } from './SahPlansView.tsx';
import type { PlanReadings } from '../../strategy/planChoiceRule.ts';

/** Support at home's readings for the plan-choice rule (planChoiceRule.ts, owner 20 Oct 2026). */
const sahReadings: PlanReadings<EvaluatedStrategy> = {
    estate: (s) => Number(s.snapshot?.legacyValue) || 0,
    moved: (s) => sahMovedAmount(s.config as never).amount,
    cashflow: (s) => Number(s.snapshot?.netCashflow ?? s.snapshot?.cashflow) || 0,
    kinds: (s) => riskKindsFromSupportAtHomeType(s.config?.type, String(s.name || '')),
};
import { SahQuestions, answersFromGoals, goalsFromAnswers, strategySettingsFromAnswers, type SahAnswers, type BindingTest } from './SahQuestions.tsx';
import { SahCauseEffect, SahFigurePopover, figuresFromSnapshot, type SahFigurePage } from './SahPlanDialogs.tsx';
import { sahPlanWords, sahLeverKinds, abpDrawChangesFromAllocation, abpDrawSentences, type SahConfigLike } from './sahPlanWords.ts';
import { sahCardRecord } from './sahChosenPlan.ts';
import SahCareUpDialog from './SahCareUpDialog.tsx';
import { sahMoveAnswer, sahPricedServicePlan, sahContextOptions, sahBaselineFactFind, sahLinkedPosition, sahLinkedCareFor, linkedResidencyOverridesOf } from './sahEngineContext.ts';
import { savedPlanTitle } from '../agedCare/planWords.ts';
import DebtsInPlanNote from '../../components/strategy/DebtsInPlanNote.tsx';
import { sahDebtTwinOf, sahDebtClearedBy } from './sahDebts.ts';
import { setModuleScreenContext, plansScreenNote } from '../../utils/moduleScreenContext.ts';
import { BuildYourselfLine } from '../../components/strategy/QuestionParts.tsx';

type DraftFactFindGoals = FactFindGoals;

/** Baseline financial position the suggestion engine measures every candidate strategy against. */
interface SahAnalysisBaseline {
    financials: Record<string, unknown>;
    contributions: SupportAtHomeContributions;
    projections: SupportAtHomeProjectionYear[];
    servicePlanCosts: { isEstimated: boolean; estimatedDescription: string | null | undefined };
    agedCareIntegrationApplied: boolean;
    /** The exact plan the analysis modelled — drives the snapshot's plan view and staleness check. */
    servicePlanUsed?: SupportAtHomeServicePlan | null;
    servicePlanIsEstimated?: boolean;
    servicePlanDescription?: string | null;
}

/** supportAtHomeRecommendedStrategies.ts's StrategySnapshot is a deliberately minimal shape shared
 * across modules; the server's evaluateSupportAtHomeStrategies response (rendered in the strategy
 * cards below) carries considerably more detail, so this extends it with the extra fields this
 * panel's UI actually reads. */
interface SahDisplayStrategySnapshot extends StrategySnapshot {
    baselineContributionRates?: { clinical?: number; independence?: number; everydayLiving?: number };
    agePensionBreakdown?: TooltipRow[];
    totalSupportAtHomeCosts?: number;
    grossServicePlanCosts?: number;
    supportAtHomeCostsBreakdown?: {
        grossAnnual?: number;
        clinical?: number;
        independence?: number;
        everydayLiving?: number;
        independenceContribution?: number;
        everydayContribution?: number;
        [key: string]: unknown;
    };
    taxBreakdown?: TooltipRow[];
    taxPayable?: number;
    abpIncome?: number;
    rentalIncome?: number;
    otherInvestmentIncome?: number;
    livingExpenses?: number;
}

/** calculateServicePlanCosts (ServicePlanUtils.ts) result, extended with the estimated-plan display
 * fields this panel adds before storing it in state. */
interface SahServicePlanCosts {
    totalAnnual?: number;
    clinical?: number;
    independence?: number;
    everyday?: number;
    hasServicePlan?: boolean;
    contribution?: { clinical?: number; independence?: number; everyday?: number; total?: number };
    hasUserServicePlan?: boolean;
    isEstimated?: boolean;
    estimatedDescription?: string | null;
    [key: string]: unknown;
}

interface SahAssetAllocationRow {
    description?: string;
    amount?: number | string;
    status?: 'sold' | 'partiallySold' | 'new' | 'upgraded' | string;
    assetName?: string;
    liquidatedAmount?: number;
    remainingAmount?: number;
    [key: string]: unknown;
}

/** A candidate strategy as evaluated by the server and rendered by this panel — extends the shared
 * EvaluatedStrategy with the additional snapshot/config/assetAllocation detail this UI reads. */
interface SahDisplayStrategy extends EvaluatedStrategy {
    snapshot?: SahDisplayStrategySnapshot;
    config?: { type?: string; description?: string; [key: string]: unknown };
    assetAllocation?: SahAssetAllocationRow[];
    recommendationLabels?: string[];
}

/** Result of a completed runAnalysis() pass — stored in `analysis` state and rendered throughout this panel. */
interface SahAnalysisPayload {
    strategies: SahDisplayStrategy[];
    recommendedOptimalStrategies: RecommendedStrategyEntry[];
    allEvaluatedStrategies: SahDisplayStrategy[];
    doNothingStrategy: SahDisplayStrategy;
    baseline: SahAnalysisBaseline;
    problemStatement: ProblemStatement;
    strategyFiltering: FilteredStrategiesResult;
    modellingAssumptions: SupportAtHomeAssumptions;
    totalGenerated: number;
    totalEvaluated: number;
    /** JSON of the effective service plan at run time — a mismatch with the current plan means the
     * results model services the user has since changed. */
    servicePlanFingerprint?: string;
    /** The household the run was priced on: whether a partner's aged care plan was carried in, and which
     * (24 Oct 2026). A run without it predates the whole plan being carried and is stale when one is. */
    householdFingerprint?: string;
}

/** Context passed down from StrategiesSection so a saved suggestion-engine strategy can be evaluated
 * with the same means-test settings as the rest of the page. */
interface HomeCareSaveContext {
    participantType: string;
    nwopCategory: string | null;
    meansDisclosed: boolean;
    previousITCF: number | string;
    isPartnered: boolean;
}

export interface SupportAtHomeSuggestionEnginePanelProps {
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
    /**
     * When Support at Home services begin (`YYYY-MM-DD`), blank = already receiving them. A fact
     * about the CASE, owned by StrategiesSection and passed down so the baseline and every candidate
     * are evaluated on the same timing — see `contributionContextOptions`.
     */
    supportStartDate?: string;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    servicePlan?: SupportAtHomeServicePlan;
    goals?: DraftFactFindGoals;
    onStrategySelect?: (strategy: Record<string, unknown>) => void;
    selectedStrategy?: Record<string, unknown> | null;
    legacyYears?: number;
    onNavigateToDashboard?: () => void;
    autoOpenOnMount?: boolean;
    onGoToServiceBuilder?: () => void;
    selectedAgedCareIntegration?: AgedCareIntegrationDetails | null;
    showBaseline?: boolean;
    setShowBaseline?: React.Dispatch<React.SetStateAction<boolean>>;
    homeCareSaveContext?: HomeCareSaveContext | null;
    onStrategySaved?: (saved: SavedStrategy) => void;
    onAnalysisStateChange?: (loading: boolean) => void;
    /** "Build a plan yourself" — Manual Control, seeded from the plan given (or empty). */
    onBuildYourOwn?: (row: Record<string, unknown> | null) => void;
    /** The plan page's "Get the advice document": open the chosen plan's document once the plans are back. */
    openAdviceWhenReady?: boolean;
    /** The plan page's "See the year-by-year figures" (from its Explain this): open the chosen plan's once the plans are back. */
    openYearByYearWhenReady?: boolean;
    /** "If care needs increase" on a plan card. */
    onCareUp?: (row: Record<string, unknown>) => void;
    /** The years answered on the questions screen, so the rest of the page follows them. */
    onLegacyYearsChange?: (years: number) => void;
    /** Which test sets the Age Pension today, for ordering the levers. */
    bindingTest?: BindingTest;
}

/** One person's side of a service plan as display rows: name, quantity, indicative annual cost. */
function servicePlanSideRows(side: Record<string, { quantity?: number; frequency?: string } | undefined> | undefined) {
    const rows: Array<{ id: string; name: string; detail: string; annual: number }> = [];
    if (!side) return rows;
    const catalogue = Object.values(SUPPORT_AT_HOME_SERVICES).flatMap((c) => c.services.map((s) => ({ ...s, category: c.label })));
    Object.entries(side).forEach(([serviceId, allocation]) => {
        const quantity = Number(allocation?.quantity) || 0;
        if (quantity <= 0) return;
        const frequency = allocation?.frequency || 'monthly';
        const entry = catalogue.find((s) => s.id === serviceId);
        // Same convention as the cost engine: 365 days and 52 weeks a year (19 Oct 2026; was ×30 / ×4 a month).
        const perMonth = frequency === 'daily' ? (quantity * 365) / 12 : frequency === 'weekly' ? (quantity * 52) / 12 : quantity;
        const annual = (entry?.medianPrice || 0) * perMonth * 12;
        rows.push({
            id: serviceId,
            name: entry?.name || serviceId,
            detail: `${quantity}/${frequency === 'daily' ? 'day' : frequency === 'weekly' ? 'week' : 'month'} · ${entry?.category || ''}`,
            annual,
        });
    });
    return rows.sort((a, b) => b.annual - a.annual);
}

/** Collapsed-by-default view of the service plan an analysis modelled — the services, how often,
 * and their indicative gross cost — so the numbers on the cards are never built on an invisible
 * plan. Renders the illustrative-package explanation when no real plan was in force. */
function ServicePlanSummaryCollapsible({ plan, isEstimated, description, isPartnered, clientName, partnerName }: {
    plan: SupportAtHomeServicePlan | null;
    isEstimated: boolean;
    description: string | null;
    isPartnered: boolean;
    clientName: string;
    partnerName: string;
}) {
    const [open, setOpen] = useState(false);
    const clientRows = servicePlanSideRows(plan?.client);
    const partnerRows = isPartnered ? servicePlanSideRows(plan?.partner) : [];
    const total = [...clientRows, ...partnerRows].reduce((sum, r) => sum + r.annual, 0);
    const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

    const renderSide = (label: string, rows: ReturnType<typeof servicePlanSideRows>) => (
        rows.length > 0 && (
            <div className="mb-3">
                {isPartnered && <p className="text-xs font-semibold text-gray-700 mb-1">{label}</p>}
                <ul className="space-y-1">
                    {rows.map((r) => (
                        <li key={r.id} className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-gray-800">{r.name} <span className="text-gray-500 text-xs">({r.detail})</span></span>
                            <span className="font-medium text-gray-900 whitespace-nowrap">{fmt(r.annual)}/yr</span>
                        </li>
                    ))}
                </ul>
            </div>
        )
    );

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
            >
                <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">
                        View service plan used in this analysis
                        <span className="ml-2 font-normal text-gray-500">
                            {isEstimated ? 'Illustrative package' : `Your plan · ${fmt(total)}/yr gross`}
                        </span>
                    </span>
                </div>
                {open ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {isEstimated && description && (
                        <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-lg px-3 py-2 mb-3">{description}</p>
                    )}
                    {renderSide(clientName, clientRows)}
                    {renderSide(partnerName, partnerRows)}
                    {clientRows.length === 0 && partnerRows.length === 0 && (
                        <p className="text-sm text-gray-600">No services recorded in this plan.</p>
                    )}
                    {(clientRows.length > 0 || partnerRows.length > 0) && (
                        <p className="text-xs text-gray-500 mt-2">
                            Gross catalogue costs at indicative median prices — your out-of-pocket contribution
                            depends on your contribution rates, shown in the panel on the right.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

const SupportAtHomeSuggestionEnginePanel = ({
    onSoaGenerated,
    supportStartDate = '',
    factFind,
    setFactFind,
    servicePlan = { client: {}, partner: {} }, // Service plan from Service Builder
    goals = {},
    onStrategySelect,
    selectedStrategy,
    legacyYears = 5,
    onNavigateToDashboard,
    autoOpenOnMount = false, // If true, open Strategy Analysis Setup when component loads
    onGoToServiceBuilder, // Optional: navigate straight to Service Builder
    selectedAgedCareIntegration = null, // Aged Care integration strategy (if selected)
    showBaseline = false,
    setShowBaseline,
    homeCareSaveContext = null,
    onStrategySaved,
    onAnalysisStateChange,
    onBuildYourOwn,
    openAdviceWhenReady = false,
    openYearByYearWhenReady = false,
    onCareUp,
    onLegacyYearsChange,
    bindingTest = null,
}: SupportAtHomeSuggestionEnginePanelProps) => {
    const [analysis, setAnalysis] = useState<SahAnalysisPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [isReoptimising, setIsReoptimising] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        onAnalysisStateChange?.(loading);
    }, [loading, onAnalysisStateChange]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showGoalPrioritization, setShowGoalPrioritization] = useState(false);
    const [goalPriority, setGoalPriority] = useState<GoalPriority>(null); // 'cashflow', 'estate', 'pension', or null
    const [deepDiveModal, setDeepDiveModal] = useState(false); // Deep dive modal state
    // "What could go wrong" for one card (StrategyRisksPanel).
    const [riskStrategy, setRiskStrategy] = useState<SahDisplayStrategy | null>(null);
    /** The plan whose "If care needs increase" is open (rebuild M4). */
    const [careUpRow, setCareUpRow] = useState<SahDisplayStrategy | null>(null);
    const [selectedStrategyForDeepDive, setSelectedStrategyForDeepDive] = useState<SahDisplayStrategy | null>(null); // Strategy for deep dive
    /*
     * THE ANSWERS DRIVE THE RUN (rebuild, 19 Oct 2026). The years, the reserve and the levers used to
     * be fixed here — 5 years and $50,000 whatever the client said — so the questions screen now owns
     * them, saved to the profile once answered (SahQuestions.goalsFromAnswers).
     */
    const suggestedReserve = useMemo(() => getRecommendedEmergencyFund(factFind), [factFind]);
    const [answers, setAnswers] = useState<SahAnswers>(() => answersFromGoals(factFind?.goals as Record<string, unknown> | undefined, getRecommendedEmergencyFund(factFind)));
    const strategySettings = useMemo(() => strategySettingsFromAnswers(answers) as unknown as SahStrategySettings, [answers]);
    const currentSettings = useMemo(() => ({ legacyYears: answers.years, emergencyFund: answers.emergencyReserve }), [answers]);
    const [questionsOpen, setQuestionsOpen] = useState(false);
    const [explainRow, setExplainRow] = useState<SahDisplayStrategy | null>(null);
    const [figureFor, setFigureFor] = useState<{ row: SahDisplayStrategy; page: SahFigurePage } | null>(null);
    /*
     * The chosen plan for THIS run, matched by the engine's own name, so returning to the plans marks
     * the card chosen. Read once from the saved Support at home plan that is chosen.
     */
    const [chosenName, setChosenName] = useState<string | null>(() => {
        const rec = getSavedHomeCareStrategies().find((s) => (s as { chosen?: boolean }).chosen && s.source === 'suggestion_engine');
        return rec ? String(((rec.inputs || {}) as Record<string, unknown>).strategyName || '') || null : null;
    });
    /*
     * ...AND THE SERVICES IT WAS PRICED ON (owner-caught, 20 Oct 2026: a fresh run opened on "You've
     * chosen 'Spend $250,000 on the home'" when nothing had been chosen in it). A plan is the same plan
     * only for the same services — as aged care's chosen plan is only for the same room. With other
     * services nothing here reads as chosen, and the earlier plan is said to stay chosen until one is.
     */
    const [chosenRecord, setChosenRecord] = useState<{ fingerprint: string | null; household: string; title: string } | null>(() => {
        const rec = getSavedHomeCareStrategies().find((s) => (s as { chosen?: boolean }).chosen && s.source === 'suggestion_engine');
        if (!rec) return null;
        const inp = (rec.inputs || {}) as Record<string, unknown>;
        return { fingerprint: typeof inp.servicePlanFingerprint === 'string' ? inp.servicePlanFingerprint : null, household: sahHouseholdOf(rec), title: String((rec as { displayName?: unknown }).displayName || rec.name || 'their earlier plan') };
    });
    const [chosenSaved, setChosenSaved] = useState<{ saved: SavedStrategy; row: SahDisplayStrategy } | null>(null);
    const [showAnnuityExplainer, setShowAnnuityExplainer] = useState(false);
    const [expandedExplanations, setExpandedExplanations] = useState<Record<string, boolean>>({
        gifting: false,
        funeralBonds: false,
        annuities: false,
        propertyStrategies: false
    });
    const [showModellingApproach, setShowModellingApproach] = useState(false); // Collapsed by default
    const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);
    const [showAllStrategiesTable, setShowAllStrategiesTable] = useState(false);
    const [showGoalLeadersModal, setShowGoalLeadersModal] = useState(false);
    const [allStrategiesSortBy, setAllStrategiesSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
    /* Escape closes the topmost overlay (review, 18 Oct 2026: it closed neither "View Details" nor
       "Compare all strategies"). The details modal opens from the two tables, so it goes first. */
    useEffect(() => {
        if (!deepDiveModal && !showGoalLeadersModal && !showAllStrategiesTable) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (deepDiveModal) setDeepDiveModal(false);
            else if (showGoalLeadersModal) setShowGoalLeadersModal(false);
            else setShowAllStrategiesTable(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [deepDiveModal, showGoalLeadersModal, showAllStrategiesTable]);
    const [showSaveStrategyModal, setShowSaveStrategyModal] = useState(false);
    const [strategyPendingSave, setStrategyPendingSave] = useState<SahDisplayStrategy | null>(null);
    const [lastSavedStrategy, setLastSavedStrategy] = useState<SavedStrategy | null>(null);
    const [saveStrategyName, setSaveStrategyName] = useState('');
    const [saveStrategyNameError, setSaveStrategyNameError] = useState('');
    const [saveStrategySuccess, setSaveStrategySuccess] = useState(false);
    const [optimisationFocus, setOptimisationFocus] = useState<'covered' | 'legacy' | 'cashflow' | 'pension' | 'all'>(() => {
        if (goals?.preserveInheritance && !goals?.meetCareCosts && !goals?.maximizeGovtSupport) return 'legacy';
        if (goals?.meetCareCosts && !goals?.preserveInheritance && !goals?.maximizeGovtSupport) return 'cashflow';
        if (goals?.maximizeGovtSupport && !goals?.preserveInheritance && !goals?.meetCareCosts) return 'pension';
        // Best estate leads (owner, 24 Sep 2026).
        return 'legacy';
    });
    const [availableAgedCareStrategies, setAvailableAgedCareStrategies] = useState<SavedStrategy[]>([]); // Available Aged Care strategies
    const [selectedAgedCareStrategyId, setSelectedAgedCareStrategyId] = useState<string | null>(null); // Selected Aged Care strategy ID
    // Step 1's answer decides it (sahEngineContext.ts): "Yes — X is moving" includes the chosen aged care plan.
    const [contributionContextMode, setContributionContextMode] = useState<'current' | 'linked'>(() => (sahMoveAnswer(factFind) === 'yes' ? 'linked' : 'current'));
    useEffect(() => {
        setContributionContextMode(sahMoveAnswer(factFind) === 'yes' ? 'linked' : 'current');
    }, [factFind?.goals?.sahAgedCareMove]); // eslint-disable-line react-hooks/exhaustive-deps
    const hasStructures = (factFind?.structures || []).length > 0;
    const [structureFundingInSuggestions, setStructureFundingInSuggestions] = useState<StructureFundingInSuggestions>(
        () => (factFind?.structureFundingInSuggestions as StructureFundingInSuggestions) || 'exclude'
    );

    useEffect(() => {
        setStructureFundingInSuggestions((factFind?.structureFundingInSuggestions as StructureFundingInSuggestions) || 'exclude');
    }, [factFind?.structureFundingInSuggestions]);

    const toggleExplanation = (strategyKey: string) => {
        setExpandedExplanations(prev => ({
            ...prev,
            [strategyKey]: !prev[strategyKey]
        }));
    };

    // Load available Aged Care strategies from sessionStorage
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const savedStrategies: SavedStrategy[] = JSON.parse(sessionStorage.getItem('careCompanionStrategies') || '[]');
                // Filter for aged care strategies
                const agedCareStrategies = savedStrategies.filter((s) => s.type === 'aged_care');
                setAvailableAgedCareStrategies(agedCareStrategies);

                // If there's a selected strategy from parent (manual integration), use it
                if (selectedAgedCareIntegration && selectedAgedCareIntegration.id) {
                    setSelectedAgedCareStrategyId(selectedAgedCareIntegration.id);
                }
            } catch (error) {
                console.error('Error loading Aged Care strategies:', error);
            }
        }
    }, [selectedAgedCareIntegration]);
    
    // Get selected Aged Care strategy details
    const selectedAgedCareStrategyForIntegration = useMemo(() => {
        if (!selectedAgedCareStrategyId) return null;
        const selectedId = String(selectedAgedCareStrategyId);
        return availableAgedCareStrategies.find((s) => String(s.id) === selectedId) || null;
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);
    
    // Extract Aged Care integration details from selected strategy
    const effectiveAgedCareIntegration = useMemo(() => {
        if (selectedAgedCareStrategyForIntegration) {
            return extractAgedCareIntegrationDetails(selectedAgedCareStrategyForIntegration);
        }
        // Fall back to prop if provided
        return selectedAgedCareIntegration;
    }, [selectedAgedCareStrategyForIntegration, selectedAgedCareIntegration]);
    const isPartnered = factFind?.adviceFor === 'couple';
    // Debts a plan can decide about (never a card paid off monthly — strategy/debtsToTest.ts).
    const clientLiabilities = useMemo(() => decidableClientLiabilities(factFind), [factFind]);
    const clientHasDebts = clientLiabilities.length > 0;
    const shouldOfferContributionContextSwitch = isPartnered && Boolean(factFind?.goals?.aged_care || effectiveAgedCareIntegration);
    const linkedResidencyOverrides = useMemo(
        () => linkedResidencyOverridesOf(effectiveAgedCareIntegration ?? null, factFind),
        [effectiveAgedCareIntegration, factFind],
    );
    // ONE builder for the options (sahEngineContext.ts), shared with steps 1 and 2: the support start
    // date rides the same bag as the residency overrides, and in the linked case the chosen aged care
    // plan's yearly fees are charged too (19 Oct 2026, pack B.SAH.6).
    const contributionContextOptions = useMemo(
        () => sahContextOptions({ supportStartDate, linked: contributionContextMode === 'linked' ? (effectiveAgedCareIntegration ?? null) : null, overrides: linkedResidencyOverrides }),
        [contributionContextMode, linkedResidencyOverrides, supportStartDate, effectiveAgedCareIntegration],
    );
    const applyLinkedAgedCareScenario = contributionContextMode === 'linked' && Boolean(effectiveAgedCareIntegration);
    // The household the plans are priced on, for the "tested on a different household" check (24 Oct 2026).
    const householdFingerprint = sahHouseholdFingerprint(applyLinkedAgedCareScenario ? String(selectedAgedCareStrategyForIntegration?.id ?? effectiveAgedCareIntegration?.id ?? '') : null);
    const canSelectLinkedContext = Boolean(selectedAgedCareIntegration) || availableAgedCareStrategies.length > 0;
    const isLinkedContextUnavailable = contributionContextMode === 'linked' && Object.keys(linkedResidencyOverrides || {}).length === 0;

    useEffect(() => {
        if (selectedAgedCareIntegration?.id) {
            setContributionContextMode('linked');
        }
    }, [selectedAgedCareIntegration]);

    // "Include the chosen aged care plan" means the CHOSEN one: picked for them, so the list only asks
    // when there is more than one saved and none is chosen (with one saved, it is that one).
    useEffect(() => {
        if (contributionContextMode !== 'linked' || selectedAgedCareStrategyId || availableAgedCareStrategies.length === 0) return;
        const chosen = availableAgedCareStrategies.find((s) => (s as { chosen?: boolean }).chosen);
        const pick = chosen || (availableAgedCareStrategies.length === 1 ? availableAgedCareStrategies[0] : null);
        if (pick?.id != null) setSelectedAgedCareStrategyId(pick.id as never);
    }, [contributionContextMode, selectedAgedCareStrategyId, availableAgedCareStrategies]);

    const openSaveStrategyModal = (strategy: SahDisplayStrategy) => {
        setStrategyPendingSave(strategy);
        setSaveStrategyName((strategy?.name || 'Suggested strategy').replace(/\s*\([^)]*\)\s*$/, '').trim());
        setSaveStrategyNameError('');
        setSaveStrategySuccess(false);
        setShowSaveStrategyModal(true);
        if (onStrategySelect) {
            onStrategySelect(strategy);
        }
    };

    const handleConfirmSaveStrategy = async () => {
        if (!strategyPendingSave || !homeCareSaveContext) return;
        const pendingAnnuity = sahPlanAnnuityOf(strategyPendingSave.config as never);
        if (pendingAnnuity && incomeOnlyHeld(factFind, pendingAnnuity.incomeOnly, pendingAnnuity.amount)) {
            setSaveStrategyNameError('This plan’s guaranteed income is the type where nothing comes back. Confirm that on its card before saving it.');
            return;
        }
        if (!saveStrategyName.trim()) {
            setSaveStrategyNameError('Please enter a strategy name');
            return;
        }
        setSaveStrategyNameError('');
        const linkedAgedCareForSave = contributionContextMode === 'linked' && effectiveAgedCareIntegration;
        const saved = await buildHomeCareStrategyFromSuggestionEngine({
            factFind,
            evaluatedStrategy: strategyPendingSave,
            name: saveStrategyName,
            selectedAgedCareIntegration: linkedAgedCareForSave ? effectiveAgedCareIntegration : null,
            agedCareResidencyOverrides: linkedAgedCareForSave ? linkedResidencyOverrides : {},
            participantType: homeCareSaveContext.participantType,
            nwopCategory: homeCareSaveContext.nwopCategory,
            meansDisclosed: homeCareSaveContext.meansDisclosed,
            previousITCF: Number(homeCareSaveContext.previousITCF) || 0,
            isPartnered: homeCareSaveContext.isPartnered,
            agedCareContext: linkedAgedCareForSave ? 'linked' : 'current',
        });
        persistHomeCareStrategy(saved);
        onStrategySaved?.(saved);
        setLastSavedStrategy(saved);
        // No auto-close: the success state offers the Digital SOA (generated from the SAVED record,
        // so the document always matches something on file — same discipline as aged care).
        setSaveStrategySuccess(true);
    };

    const closeSaveStrategyModal = () => {
        setShowSaveStrategyModal(false);
        setStrategyPendingSave(null);
        setSaveStrategySuccess(false);
        setLastSavedStrategy(null);
    };

    /** Build and open the Digital SOA for the just-saved strategy, and persist the snapshot onto it
     * so the exact document can be reopened later. */
    const openDigitalSoaForSavedStrategy = async (
        saved: SavedStrategy | null = lastSavedStrategy,
        pending: SahDisplayStrategy | null = strategyPendingSave,
    ) => {
        if (!saved) return;
        const snapshot = await buildSupportAtHomeDigitalSOASnapshot({
            selectedStrategy: saved,
            factFind,
            projections: ((pending as { projections?: unknown } | null)?.projections ?? null) as never,
            analysis: analysis ? { strategies: analysis.strategies as never, totalEvaluated: analysis.totalEvaluated } : null,
            servicePlan: servicePlanResolution.plan,
            servicePlanIsEstimated: servicePlanResolution.isEstimated,
            servicePlanDescription: servicePlanResolution.description,
            strategySource: 'suggestion_engine',
            linkedCare: applyLinkedAgedCareScenario ? await sahLinkedCareFor(factFind, selectedAgedCareStrategyForIntegration) : null,
        });
        if (snapshot) {
            const document = { ...saved, soaSnapshot: snapshot };
            persistHomeCareStrategy(document as never);
            onSoaGenerated?.(document as Record<string, unknown>);
            openSupportAtHomeDigitalSOAAttachment(snapshot);
        }
        setDeferredSoa(null);
        closeSaveStrategyModal();
    };
    /* "Maybe later" no longer loses the offer (review, 18 Oct 2026: after it there was no way back to the
       advice document on the page). The saved plan and its projections are kept, and a line under the
       plans offers the document until it is opened. */
    const [deferredSoa, setDeferredSoa] = useState<{ saved: SavedStrategy; pending: SahDisplayStrategy | null } | null>(null);
    const deferSoaForSavedStrategy = () => {
        if (lastSavedStrategy) setDeferredSoa({ saved: lastSavedStrategy, pending: strategyPendingSave });
        closeSaveStrategyModal();
    };

    // Auto-open Strategy Analysis Setup when Suggestion Engine tab is first selected
    // — the FIRST time only (review, 18 Oct 2026: it opened again on every visit, minutes after it was
    // answered). Once answered, the plans run on arrival instead.
    useEffect(() => {
        if (!autoOpenOnMount) return;
        // Not yet answered: the questions show inline (no pop-over), and the plans run from there.
        // Answered already, including how freed-up money is invested — so the one-off gate is not shown again.
        if ((factFind?.goals as { sahSetupSeen?: boolean } | undefined)?.sahSetupSeen && !analysis) maybeRunAnalysis(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpenOnMount]);

    // Check if all goals are selected
    useEffect(() => {
        const allGoalsSelected = goals.meetCareCosts && goals.preserveInheritance && goals.maximizeGovtSupport;
        if (allGoalsSelected && !goalPriority) {
            setShowGoalPrioritization(true);
        }
    }, [goals, goalPriority]);

    // Format currency helper
    const formatCurrency = (value: number | string | null | undefined): string => {
        const num = Number(value);
        if (!value || isNaN(num)) return '$0';
        const roundedValue = Math.round(num);
        return `${roundedValue < 0 ? '-' : ''}$${Math.abs(roundedValue).toLocaleString()}`;
    };

    // Which service plan the engine models. Default: the current Service Builder plan (the
    // `servicePlan` prop, read from session storage by the parent). The user can instead pick one of
    // their SAVED service plans in Strategy Analysis Setup — choosing one also becomes the app-wide
    // "current plan" so the builder, insight panel and engine all agree on what's in force.
    const [servicePlanOverride, setServicePlanOverride] = useState<SupportAtHomeServicePlan | null>(null);
    const [servicePlanOverrideId, setServicePlanOverrideId] = useState<string>('');
    // With "Yes — X is moving into aged care" (step 1), X has no Support at home services priced.
    const effectiveServicePlan = useMemo(() => sahPricedServicePlan(servicePlanOverride ?? servicePlan, factFind), [servicePlanOverride, servicePlan, factFind]);

    /** Saved home-care strategies that actually carry a service plan, normalised to {client, partner}. */
    const savedServicePlans = useMemo(() => {
        const toSides = (services: Record<string, unknown>): SupportAtHomeServicePlan =>
            (services.client || services.partner)
                ? { client: (services.client || {}) as SupportAtHomeServicePlan['client'], partner: (services.partner || {}) as SupportAtHomeServicePlan['partner'] }
                : { client: services as SupportAtHomeServicePlan['client'], partner: {} };
        const hasAny = (plan: SupportAtHomeServicePlan) =>
            [plan.client, plan.partner].some((side) => Object.values(side || {}).some((d) => Number(d?.quantity) > 0));
        return getSavedHomeCareStrategies()
            .filter((s) => s?.inputs?.services)
            .map((s) => ({ id: String(s.id), name: s.name || 'Saved plan', plan: toSides(s.inputs!.services as Record<string, unknown>) }))
            .filter((entry) => hasAny(entry.plan));
    }, []);

    const selectSavedServicePlan = (id: string) => {
        setServicePlanOverrideId(id);
        const chosen = savedServicePlans.find((p) => p.id === id) || null;
        setServicePlanOverride(chosen ? chosen.plan : null);
        if (chosen && typeof window !== 'undefined') {
            try {
                sessionStorage.setItem('careCompanionCurrentServicePlan', JSON.stringify(chosen.plan));
            } catch { /* storage full/blocked — the in-memory override still drives this run */ }
        }
    };

  // Service plan for modelling (user plan or documented illustrative mix)
    const servicePlanResolution = useMemo(() => {
        const isPartnered = factFind?.adviceFor === 'couple';
        return resolveServicePlanForSuggestionEngine(effectiveServicePlan, isPartnered);
    }, [effectiveServicePlan, factFind?.adviceFor]);

    const [servicePlanCosts, setServicePlanCosts] = useState<SahServicePlanCosts>({});
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const baselineFinancials = await calculationApi.calculateFinancials(factFind, contributionContextOptions.strategyOptions || {});
            const baselineContributions = await calculationApi.calculateIndividualContributions(
                factFind,
                baselineFinancials,
                'standard',
                null,
                true,
                0,
                0.5,
                contributionContextOptions
            );
            if (cancelled) return;

            const isPartnered = factFind?.adviceFor === 'couple';
            const contributionRates = isPartnered
                ? (baselineContributions?.combined?.contributionRates || {})
                : (baselineContributions?.client?.contributionRates || {});

            const costs = calculateServiceCostsUtil(
                servicePlanResolution.plan,
                contributionRates,
                isPartnered,
                servicePlanBudgetsOf(factFind),
            );

            setServicePlanCosts({
                ...costs,
                hasUserServicePlan: !servicePlanResolution.isEstimated,
                isEstimated: servicePlanResolution.isEstimated,
                estimatedDescription: servicePlanResolution.description,
            });
        })();
        return () => { cancelled = true; };
    }, [servicePlanResolution, factFind, contributionContextOptions]);

    /**
     * The just-in-time "how would new money be invested?" gate. Downsize strategies deploy the
     * freed-up money, so when the client has a home and no recorded answer the question is asked
     * BEFORE the engine prices anything — same rules as the village module (answering saves to
     * the profile and is asked once ever; skipping runs at Conservative without recording).
     */
    const [showNewMoneyGate, setShowNewMoneyGate] = useState(false);
    const [newMoneySkipped, setNewMoneySkipped] = useState(false);
    /*
     * THE GUARANTEED-INCOME QUESTION (owner, 21 Oct 2026 — the guaranteed income review; mock-up screen 6). It is
     * asked on the questions screen (SahQuestions → GuaranteedIncomeQuestions), not as a gate before the run:
     * the explanation always on screen, three answers, and how much at most. "No" removes every guaranteed
     * income from this and every other module; the answer is the client's (annuityPreference.ts), written
     * straight to their goals and never overwritten with a default.
     */
    const writeGuaranteedIncomeGoals = (patch: GuaranteedIncomeGoals) => {
        if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), ...patch } as never }));
    };
    /** The money a guaranteed income could come from, as the plans size it (the same figure the question shows). */
    const annuityInvestable = useMemo(() => sahAnnuityInvestable(factFind, answers.emergencyReserve), [factFind, answers.emergencyReserve]);
    /** The client's confirmation for a tested income-only plan (IncomeOnlyTick.tsx). */
    const setIncomeOnlyConfirmed = incomeOnlySetter(setFactFind);
    const [showTbcGate, setShowTbcGate] = useState(false);
    const [tbcSkipped, setTbcSkipped] = useState(false);
    const sahRunWouldInvest = (parseFloat(String(factFind?.home?.value ?? '')) || 0) > 0;
    // Chained after the new-money question: SAH's downsize strategies can commence a pension from
    // the downsizer contribution, so the $1.6m screen asks for the ATO cap figure before pricing.
    const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && sahRunWouldInvest
        ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
            : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
        : null;
    /**
     * @param askedInSetup - the caller has just shown the settings modal, which carries the
     * new-money question itself. Its default option ("Not sure — assume Conservative") is a
     * legitimate answer that deliberately records nothing, so gating on a RECORDED answer put the
     * interim screen back in front of every adviser who left the dropdown alone — the exact screen
     * moving the question into the setup was meant to remove. The gate below stays for any future
     * caller that runs without opening the modal.
     */
    const maybeRunAnalysis = (askedInSetup = false) => {
        if (!askedInSetup && sahRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped) {
            setShowNewMoneyGate(true);
            return;
        }
        if (tbcPersonNeedingAnswer !== null) {
            setShowTbcGate(true);
            return;
        }
        runAnalysis();
    };
    const answerNewMoneyProfile = (id: InvestmentProfileId) => {
        setShowNewMoneyGate(false);
        if (setFactFind) {
            setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
        }
        if (tbcPersonNeedingAnswer !== null) { setShowTbcGate(true); return; }
        runAnalysis();
    };
    const skipNewMoneyProfile = () => {
        setShowNewMoneyGate(false);
        setNewMoneySkipped(true);
        if (tbcPersonNeedingAnswer !== null) { setShowTbcGate(true); return; }
        runAnalysis();
    };
    const answerTbc = (amount: number) => {
        setShowTbcGate(false);
        if (setFactFind && tbcPersonNeedingAnswer) {
            const key = tbcPersonNeedingAnswer;
            setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
        }
        runAnalysis();
    };
    const skipTbc = () => {
        setShowTbcGate(false);
        setTbcSkipped(true);
        runAnalysis();
    };

    // Run intelligent modelling analysis
    const runAnalysis = async () => {
        setIsReoptimising(!!analysis);
        setLoading(true);
        setError(null);
        setProgress({ current: 0, total: 0 });

        const runWork = async (): Promise<{ payload: SahAnalysisPayload; hasStrategies: boolean }> => {
            // STEP 0: the household priced — in the linked case the whole aged care plan carried out (owner,
            // 24 Oct 2026; the deposit alone if it cannot be rebuilt) and the plan's living costs
            // (sahEngineContext.ts, the same builder steps 1 and 2 use).
            const linkedRecordForRun = applyLinkedAgedCareScenario ? selectedAgedCareStrategyForIntegration : null;
            const positioned = linkedRecordForRun ? (await sahLinkedPosition(factFind, linkedRecordForRun)).factFind : null;
            const baselineFactFind: DraftFactFind = sahBaselineFactFind(
                factFind,
                applyLinkedAgedCareScenario ? (effectiveAgedCareIntegration ?? null) : null,
                hasStructures ? structureFundingInSuggestions : undefined,
                positioned,
            );
            
            // STEP 1: Analyze baseline and identify problems (using Aged Care-adjusted factFind)
            const baselineAnalysis = await analyzeBaselineOpportunities(
                baselineFactFind,
                effectiveServicePlan,
                currentSettings.legacyYears || legacyYears || 5,
                {
                    strategyOptions: contributionContextOptions.strategyOptions || {},
                    contributionOptions: contributionContextOptions
                }
            );

            // STEP 2: Identify irrelevant strategies based on baseline analysis
            const strategyFiltering = identifyIrrelevantStrategies(
                baselineAnalysis.problemStatement,
                strategySettings
            );

            // STEP 3: Generate all relevant strategies (using Aged Care-adjusted factFind)
            const allStrategies = generateAllStrategies(
                baselineFactFind,
                baselineAnalysis.problemStatement,
                strategySettings
            );

            const evaluationOptions = {
                strategyOptions: contributionContextOptions.strategyOptions || {},
                contributionOptions: contributionContextOptions
            };

            setProgress({ current: 0, total: allStrategies.length + 1 });

            // STEP 4: Evaluate "Do Nothing" + every candidate strategy in ONE request (server-side
            // composite endpoint — see server/src/rest/calculation/suggestionEngine/supportAtHome.ts)
            // instead of one request per candidate.
            const baselineData = {
                baselineFinancials: baselineAnalysis.baselineFinancials,
                baselineContributions: baselineAnalysis.baselineContributions,
                baselineProjections: baselineAnalysis.baselineProjections,
            };
            const [doNothingStrategy, ...evaluatedStrategies] = await calculationApi.evaluateSupportAtHomeStrategies(
                baselineFactFind,
                baselineData,
                effectiveServicePlan as unknown as Record<string, unknown>,
                currentSettings.legacyYears || legacyYears || 5,
                [{ type: 'doNothing', name: 'Do Nothing' }, ...allStrategies],
                evaluationOptions
            );

            setProgress({ current: allStrategies.length + 1, total: allStrategies.length + 1 });

            // STEP 5: Rank strategies by goal priority (always include Do Nothing baseline). rankStrategies'
            // own BaselineAnalysis type is a loosely-typed subset shape (see SupportAtHomeStrategyEvaluator.ts);
            // baselineAnalysis here is the richer BaselineOpportunitiesResult, which is structurally a superset.
            const rankedOthers = rankStrategies(
                evaluatedStrategies,
                goalPriority,
                baselineAnalysis as unknown as BaselineAnalysis
            );
            const recommendedOptimalStrategies = buildRecommendedOptimalStrategies(
                evaluatedStrategies,
                doNothingStrategy,
                { readings: sahReadings }
            );
            const rankedStrategies = enrichStrategiesWithRecommendationLabels(
                [doNothingStrategy, ...rankedOthers],
                recommendedOptimalStrategies
            );
            
            // STEP 6: Store results
            const modellingAssumptions = buildSupportAtHomeAssumptions({
                factFind: baselineFactFind,
                strategySettings,
                legacyYears: currentSettings.legacyYears || legacyYears || 5,
                emergencyFund: currentSettings.emergencyFund || 50000,
                agedCareIntegrationApplied: applyLinkedAgedCareScenario,
                usesEstimatedServicePlan: baselineAnalysis.servicePlanResolution?.isEstimated,
                estimatedServicePlanDescription: baselineAnalysis.servicePlanResolution?.description,
            });

            return {
                payload: {
                    strategies: rankedStrategies,
                    recommendedOptimalStrategies,
                    allEvaluatedStrategies: evaluatedStrategies,
                    doNothingStrategy,
                    baseline: {
                        financials: baselineAnalysis.baselineFinancials,
                        contributions: baselineAnalysis.baselineContributions,
                        projections: baselineAnalysis.baselineProjections,
                        servicePlanCosts: baselineAnalysis.servicePlanCosts,
                        agedCareIntegrationApplied: applyLinkedAgedCareScenario,
                        // The exact plan this analysis modelled — for the snapshot's "view service
                        // plan" and for detecting that the plan changed after the run.
                        servicePlanUsed: baselineAnalysis.servicePlanResolution?.plan ?? null,
                        servicePlanIsEstimated: baselineAnalysis.servicePlanResolution?.isEstimated ?? true,
                        servicePlanDescription: baselineAnalysis.servicePlanResolution?.description ?? null,
                    },
                    servicePlanFingerprint: JSON.stringify(effectiveServicePlan ?? {}),
                    householdFingerprint,
                    problemStatement: baselineAnalysis.problemStatement,
                    strategyFiltering,
                    modellingAssumptions,
                    totalGenerated: allStrategies.length,
                    totalEvaluated: evaluatedStrategies.length,
                },
                hasStrategies: rankedStrategies.length > 0,
            };
        };

        try {
            // No minimum display time (owner, 20 Oct 2026: a saved plan should open at once). The loader
            // used to stay up for at least five seconds even when the results came straight back; the
            // engine's results are now kept (calculationApi), so it shows only while there is real work.
            const workResult = await runWork();

            if (workResult?.payload) {
                setAnalysis(workResult.payload);
            }

            // No intermediary "Analysis Complete" modal — the results render directly below,
            // exactly as the aged care module does after its analysis run.
            if (workResult && !workResult.hasStrategies) {
                setError('No viable strategies found for your current situation. Please adjust your strategy settings or check your financial position.');
            }
        } catch (err) {
            console.error('Error running Support at Home analysis:', err);
            setError('Failed to run analysis. Please try again.');
        } finally {
            setLoading(false);
            setIsReoptimising(false);
            setProgress({ current: 0, total: 0 });
        }
    };

    const recommendedOptimal = analysis?.recommendedOptimalStrategies || [];

    const effectiveOptimisationSurface = optimisationFocus === 'all' ? 'legacy' : optimisationFocus;

    const displayedGoalStrategies = useMemo(() => {
        if (!analysis?.strategies?.length) return [] as SahDisplayStrategy[];
        // A goal leader only exists where a strategy BEATS "Do Nothing" on that goal
        // (goalLeaderFor returns null otherwise, and the empty state below says so honestly
        // with the do-nothing figures). Best legacy and All goals share the legacy surface —
        // single primary card, AC parity.
        // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
        if (effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(recommendedOptimal)) return [] as SahDisplayStrategy[];
        const row = goalLeaderFor(recommendedOptimal, effectiveOptimisationSurface);
        if (!row) return [] as SahDisplayStrategy[];
        return [(analysis.strategies.find((s) => s.name === row.strategy.name) || row.strategy) as SahDisplayStrategy];
    }, [analysis?.strategies, recommendedOptimal, effectiveOptimisationSurface]);

    useEffect(() => {
        setShowAllStrategiesTable(false);
    }, [optimisationFocus]);

    const goalComparisonRows = useMemo(() => {
        const enrich = (label: string): SahDisplayStrategy | null => {
            const row = recommendedOptimal.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            return (analysis?.strategies?.find((s) => s.name === row.strategy.name) || row.strategy) as SahDisplayStrategy;
        };
        const rows: { goalLabel: string; strategy: SahDisplayStrategy }[] = [];
        const cov = enrich(CASHFLOW_COVERED_LABEL);
        if (cov) rows.push({ goalLabel: CASHFLOW_COVERED_CHIP, strategy: cov });
        const leg = enrich('Optimal Legacy');
        if (leg) rows.push({ goalLabel: BEST_ESTATE_CHIP, strategy: leg });
        const cf = enrich('Optimal Cashflow');
        if (cf) rows.push({ goalLabel: 'Best cashflow', strategy: cf });
        const pen = enrich('Optimal Age Pension');
        if (pen) rows.push({ goalLabel: 'Best Age Pension', strategy: pen });
        return rows;
    }, [recommendedOptimal, analysis?.strategies]);

    // AWise reads the plans on screen and what each does about the debts (owner, 22 Oct 2026).
    useEffect(() => {
        if (!goalComparisonRows.length) { setModuleScreenContext(null); return undefined; }
        const all = (analysis?.allEvaluatedStrategies || analysis?.strategies || []) as SahDisplayStrategy[];
        const fig = (row: SahDisplayStrategy) => { const f = figuresFromSnapshot((row.snapshot || {}) as Record<string, unknown>); return { estate: f.legacy, cashflow: f.cashflow, pension: f.pension }; };
        const base = analysis?.doNothingStrategy?.snapshot ? fig(analysis.doNothingStrategy as SahDisplayStrategy) : null;
        setModuleScreenContext({
            page: 'HomeCarePage',
            screen: 'support-at-home',
            note: plansScreenNote({
                factFind, years: Number(answers.years) || 10, baseline: base, baselineLabel: 'The support alone (the same services, nothing else changed)',
                plans: goalComparisonRows.map((r) => {
                    const twin = sahDebtTwinOf(r.strategy, all);
                    return { label: r.goalLabel, title: sahPlanWords(r.strategy.config as SahConfigLike, factFind, String(r.strategy.name || '')).title, figures: fig(r.strategy), clearedAmount: sahDebtClearedBy(r.strategy.config as SahConfigLike), twin: twin ? fig(twin) : null };
                }),
            }),
        });
        return () => setModuleScreenContext(null);
    }, [goalComparisonRows, analysis, factFind, answers.years]);

    const tableStrategies = useMemo(() => {
        const base = (analysis?.allEvaluatedStrategies || analysis?.strategies || []).filter(
            (s) => s.name !== 'Do Nothing' && s.config?.type !== 'doNothing'
        );
        const sorted = [...base];
        if (allStrategiesSortBy === 'cashflow') {
            sorted.sort(
                (a, b) =>
                    (b.snapshot?.netCashflow ?? b.snapshot?.cashflow ?? -Infinity) -
                    (a.snapshot?.netCashflow ?? a.snapshot?.cashflow ?? -Infinity)
            );
        } else if (allStrategiesSortBy === 'pension') {
            sorted.sort(
                (a, b) =>
                    (b.snapshot?.pension ?? b.snapshot?.agePension ?? 0) -
                    (a.snapshot?.pension ?? a.snapshot?.agePension ?? 0)
            );
        } else {
            sorted.sort(
                (a, b) => (b.snapshot?.legacyValue ?? -Infinity) - (a.snapshot?.legacyValue ?? -Infinity)
            );
        }
        return sorted;
    }, [analysis, allStrategiesSortBy]);

    const assumptionsModalBullets = useMemo(
        () => flattenSupportAtHomeAssumptionsForModal(analysis?.modellingAssumptions),
        [analysis?.modellingAssumptions]
    );

    const saSuggestionCardHighlightKey = useMemo(() => {
        const inner = (selectedStrategy?.strategy ?? selectedStrategy) as SahDisplayStrategy | null | undefined;
        if (!inner?.name || !displayedGoalStrategies.length) return null;
        const k = String(inner.name);
        return displayedGoalStrategies.some((s) => String(s.name) === k) ? k : null;
    }, [selectedStrategy, displayedGoalStrategies]);

    /*
     * THE OLD CARD'S BODY, KEPT BEHIND "Show the detail" (rebuild, 19 Oct 2026). The card front is now
     * the aged care card (SahPlansView); everything this card used to show — the plain-words story,
     * the allocation, the rate changes, the annual snapshot with its pop-overs, the improvements, the
     * annuity and equity-release terms — is one tap away, as aged care does it.
     */
    const renderStrategyDetail = (strategy: SahDisplayStrategy) => (
        <div data-testid="sah-plan-detail">
            {/* The card's STORY — the same plain-words principle as every other module's
                cards. The what/how/consider narratives previously reached the reader only
                via the insight panel (which is being retired); they now live on the card,
                with the longer layers a tap away. */}
            {(() => {
                const explainer = SAH_STRATEGY_EXPLAINERS[String(strategy.config?.type || '')];
                if (!explainer) {
                    return strategy.config?.description ? (
                        <p className="text-sm text-gray-600 mb-6">{strategy.config.description}</p>
                    ) : null;
                }
                /*
                 * Two additions the owner asked for (2026-08-25), both data-honest:
                 * the rates sentence only renders when THIS strategy's rates actually
                 * fall (quoting the same snapshot figures as the rates box below), and
                 * the risk line surfaces the strategy's own vetted first
                 * what-to-weigh-up point instead of boilerplate.
                 */
                const ratesNow = strategy.snapshot?.contributionRates;
                const ratesBase = strategy.snapshot?.baselineContributionRates;
                const rateFell = (key: 'independence' | 'everydayLiving') =>
                    !!ratesNow && !!ratesBase && (ratesNow[key] ?? 0) < (ratesBase[key] ?? 0) - 0.0005;
                const ratesImproved = rateFell('independence') || rateFell('everydayLiving');
                const asPct = (v: number | undefined) => `${((v ?? 0) * 100).toFixed(1)}%`;
                const keyRisk = explainer.consider[0];
                const moreToWeighUp = explainer.consider.slice(1).join(' ');
                return (
                    <PlainWordsCard eyebrow="What this strategy does" className="mb-6">
                        <p className="text-sm leading-relaxed text-slate-700">
                            {explainer.what}
                            {ratesImproved && ratesNow && ratesBase && (
                                <>
                                    {' '}It also lowers the Support at Home contribution rates {'—'}{' '}
                                    independence {asPct(ratesBase.independence)} {'→'}{' '}
                                    <span className="font-semibold text-slate-900">{asPct(ratesNow.independence)}</span>,
                                    everyday living {asPct(ratesBase.everydayLiving)} {'→'}{' '}
                                    <span className="font-semibold text-slate-900">{asPct(ratesNow.everydayLiving)}</span>{' '}
                                    {'—'} so every hour of service costs less (detailed below).
                                </>
                            )}
                        </p>
                        {keyRisk && (
                            <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                                <span className="font-semibold text-amber-700">Worth knowing:</span> {keyRisk}
                            </p>
                        )}
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                            Tap for the detail:{' '}
                            <JargonTerm term="how it works">{explainer.how.join(' ')}</JargonTerm>
                            {moreToWeighUp && (
                                <>
                                    {' · '}
                                    <JargonTerm term="more to weigh up">{moreToWeighUp}</JargonTerm>
                                </>
                            )}
                        </p>
                    </PlainWordsCard>
                );
            })()}

            {/* Asset Allocation - Show all assets modified/liquidated (matching Aged Care format) */}
            {strategy.assetAllocation && strategy.assetAllocation.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Asset Allocation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {strategy.assetAllocation.map((asset, assetIndex) => (
                            <div key={assetIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                    <div className={`w-3 h-3 rounded-full mr-3 ${
                                        asset.status === 'sold' ? 'bg-red-500' : 
                                        asset.status === 'partiallySold' ? 'bg-yellow-500' : 
                                        asset.status === 'new' ? 'bg-blue-500' :
                                        asset.status === 'upgraded' ? 'bg-green-500' :
                                        'bg-green-500'
                                    }`}></div>
                                    <span className="font-medium text-gray-700">
                                        {allocationName(asset as { assetName?: string; ownership?: string }, factFind)}
                                    </span>
                                    <span className="ml-2 text-xs text-gray-500">{assetStatusWords(asset.status)}</span>
                                </div>
                                <div className="text-right">
                                    {(asset.liquidatedAmount || 0) > 0 && (
                                        <div className="text-sm text-red-600 mb-1">
                                            Sold: -{formatCurrency(asset.liquidatedAmount)}
                                        </div>
                                    )}
                                    <div className="font-semibold text-gray-900">
                                        Kept: {formatCurrency(asset.remainingAmount)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Contribution Rate Changes */}
            {strategy.snapshot?.contributionRates && strategy.snapshot?.baselineContributionRates && (
                <div className="mb-6 p-4 bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Contribution Rate Changes</h4>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                        <div>
                            <div className="text-gray-600 mb-1">Clinical</div>
                            <div className="font-semibold">
                                {((strategy.snapshot.baselineContributionRates.clinical ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.clinical ?? 0) * 100).toFixed(1)}%
                            </div>
                            <div className={`text-xs ${(strategy.snapshot.contributionRates.clinical || 0) < (strategy.snapshot.baselineContributionRates.clinical || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                {((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) !== 0 ? 
                                    `${((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) * 100).toFixed(1)}%` : 
                                    'No change'}
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-600 mb-1">Independence</div>
                            <div className="font-semibold">
                                {((strategy.snapshot.baselineContributionRates.independence ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.independence ?? 0) * 100).toFixed(1)}%
                            </div>
                            <div className={`text-xs ${(strategy.snapshot.contributionRates.independence || 0) < (strategy.snapshot.baselineContributionRates.independence || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                {((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) !== 0 ? 
                                    `${((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) * 100).toFixed(1)}%` : 
                                    'No change'}
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-600 mb-1">Everyday Living</div>
                            <div className="font-semibold">
                                {((strategy.snapshot.baselineContributionRates.everydayLiving ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.everydayLiving ?? 0) * 100).toFixed(1)}%
                            </div>
                            <div className={`text-xs ${(strategy.snapshot.contributionRates.everydayLiving || 0) < (strategy.snapshot.baselineContributionRates.everydayLiving || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                {((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) !== 0 ? 
                                    `${((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) * 100).toFixed(1)}%` : 
                                    'No change'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Matching the Aged Care format, which is how it inherited the same mislabel:
            these are full-year RATES, while the first projection year starts today and
            is shorter. Calling them "Year 1" put two different numbers under one name —
            this tile and the year-by-year table. Same wording as the aged care card and
            the Financial Impact panel, so the product has one vocabulary for it. */}
            <div className="mb-6">
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                <h4 className="text-lg font-semibold text-gray-800">Annual Snapshot</h4>
                <span className="text-xs text-gray-500">A normalised year</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
                A full 12 months with this plan in place, so every strategy compares like for like.
                It is not the first financial year, which starts today and is shorter.
            </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                        <CalculationTooltip
                            title="Age Pension (p.a.) Breakdown"
                            data={strategy.snapshot?.agePensionBreakdown || []}
                        >
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                Est. Age Pension
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                            </div>
                        </CalculationTooltip>
                        <div className="font-medium text-green-600">
                            + {formatCurrency(strategy.snapshot?.pension || strategy.snapshot?.agePension || 0)}
                        </div>
                    </div>
                    
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Investment Income</div>
                        <div className="font-medium text-green-600">
                            + {formatCurrency((strategy.snapshot?.abpIncome || 0) + (strategy.snapshot?.rentalIncome || 0) + (strategy.snapshot?.otherInvestmentIncome || 0))}
                        </div>
                    </div>
                    
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Living Expenses</div>
                        <div className="font-medium text-red-600">
                            - {formatCurrency(strategy.snapshot?.livingExpenses || 0)}
                        </div>
                    </div>
                    
                    <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                        <CalculationTooltip
                            title="Support at Home contribution (your cost)"
                            data={[
                                { label: 'Your annual contribution', value: formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0) },
                                { label: 'Gross service fees (catalogue)', value: formatCurrency(strategy.snapshot?.grossServicePlanCosts || strategy.snapshot?.supportAtHomeCostsBreakdown?.grossAnnual || 0) },
                                { label: '--- Gross by category ---', value: '' },
                                { label: 'Clinical', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.clinical || 0) },
                                { label: 'Independence', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.independence || 0) },
                                { label: 'Everyday Living', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.everydayLiving || 0) },
                                { label: '--- Your contribution ---', value: '' },
                                { label: 'Independence contribution', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.independenceContribution || 0) },
                                { label: 'Everyday living contribution', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.everydayContribution || 0) },
                                ...((strategy.snapshot?.supportAtHomeCostsBreakdown as { privateExcess?: number } | undefined)?.privateExcess
                                    ? [{ label: 'Above the budget, paid at full price', value: formatCurrency((strategy.snapshot?.supportAtHomeCostsBreakdown as { privateExcess?: number }).privateExcess || 0) }]
                                    : []),
                                { label: 'Change vs baseline', value: formatCurrency(strategy.snapshot?.contributionReduction || 0) },
                            ]}
                        >
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                SAH contribution
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                            </div>
                        </CalculationTooltip>
                        <div className="font-medium text-red-600">
                            - {formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0)}
                        </div>
                    </div>
                    
                    <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                        <CalculationTooltip
                            title="Tax Breakdown"
                            data={strategy.snapshot?.taxBreakdown || [{ label: 'No tax payable.', value: '' }]}
                        >
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                Income Tax
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                            </div>
                        </CalculationTooltip>
                        <div className={`font-medium ${(strategy.snapshot?.taxPayable ?? 0) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(strategy.snapshot?.taxPayable ?? 0) < 0 ? '+' : '-'} {formatCurrency(Math.abs(strategy.snapshot?.taxPayable ?? 0))}
                            {(strategy.snapshot?.taxPayable ?? 0) < 0 ? ' (refund)' : ''}
                        </div>
                    </div>
                    
                    {(() => {
                        /* The tiles must add up to the net figure (review, 18 Oct 2026: six tiles added to
                           $22,846 against a stated $2,277). Rental expenses and loan repayments had no tile,
                           and the net is the money the year actually paid: the Age Pension in it is
                           re-assessed each fortnight, so it is not quite the opening rate on the tile. Both
                           are now shown, line by line, and the tiles close on the net. */
                        const other = cashflowNotOnTiles(strategy.snapshot);
                        if (Math.abs(other) < 1) return null;
                        const rows = otherCashflowLines(strategy.snapshot?.cashFlowBreakdown, other);
                        return (
                            <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                                <CalculationTooltip title="Everything else in the year" data={rows.map((r) => ({ label: r.label, value: formatCurrency(r.value) }))}>
                                    <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                        Property costs, repayments and other
                                        <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                                    </div>
                                </CalculationTooltip>
                                <div className={`font-medium ${other >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {other >= 0 ? '+' : '-'} {formatCurrency(Math.abs(other))}
                                </div>
                            </div>
                        );
                    })()}

                    <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                        <CalculationTooltip
                            title="Net Annual Cashflow Breakdown"
                            data={(() => {
                                const other = cashflowNotOnTiles(strategy.snapshot);
                                return [
                                    { label: 'Age Pension', value: formatCurrency(strategy.snapshot?.pension || strategy.snapshot?.agePension || 0) },
                                    { label: 'Investment Income', value: formatCurrency((strategy.snapshot?.abpIncome || 0) + (strategy.snapshot?.rentalIncome || 0) + (strategy.snapshot?.otherInvestmentIncome || 0)) },
                                    { label: '--- Less ---', value: '' },
                                    { label: 'Living Expenses', value: formatCurrency(strategy.snapshot?.livingExpenses || 0) },
                                    { label: 'SAH contribution (your cost)', value: formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0) },
                                    { label: (strategy.snapshot?.taxPayable ?? 0) < 0 ? 'Income Tax (refund)' : 'Income Tax', value: formatCurrency(Math.abs(strategy.snapshot?.taxPayable ?? 0)) },
                                    ...(Math.abs(other) >= 1 ? [{ label: '--- Everything else ---', value: '' }, ...otherCashflowLines(strategy.snapshot?.cashFlowBreakdown, other).map((r) => ({ label: r.label, value: formatCurrency(r.value) }))] : []),
                                    { label: '--- Result ---', value: '' },
                                    { label: 'Net Annual Cashflow', value: formatCurrency(strategy.snapshot?.cashflow || 0) },
                                ];
                            })()}
                        >
                            <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                Net Cashflow
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                            </div>
                        </CalculationTooltip>
                        <div className={`font-medium ${(strategy.snapshot?.cashflow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(strategy.snapshot?.cashflow || 0)}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Improvement Metrics */}
            <div className="mb-6 p-4 bg-white rounded-lg border border-slate-200/70 border-l-4 border-blue-500">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Key Improvements vs Baseline</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Pension Increase</div>
                        <div className={`text-lg font-semibold ${(strategy.snapshot?.agePensionIncrease || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                            {formatCurrency(strategy.snapshot?.agePensionIncrease || 0)}/yr
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Contribution Reduction</div>
                        <div className={`text-lg font-semibold ${(strategy.snapshot?.contributionReduction || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                            {formatCurrency(strategy.snapshot?.contributionReduction || 0)}/yr
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Cashflow Improvement</div>
                        <div className={`text-lg font-semibold ${(strategy.snapshot?.cashflowImprovement || 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(strategy.snapshot?.cashflowImprovement || 0)}/yr
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Legacy Improvement</div>
                        <div className={`text-lg font-semibold ${(strategy.snapshot?.legacyImprovement || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                            {formatCurrency(strategy.snapshot?.legacyImprovement || 0)}
                        </div>
                    </div>
                </div>
            </div>
            
            {/*
              * THE ANNUITY'S TERMS, where this strategy buys one (owner,
              * 2026-09-01). The card names the purchase amount and stops; the
              * owner, the residual, and whether the income survives the owner
              * were engine defaults with nothing on screen to say so.
              */}
            <EngineAnnuityTerms
                className="mb-3"
                terms={(strategy as { annuityTerms?: Record<string, unknown> }).annuityTerms}
                factFind={factFind}
            />

            {/*
              * BORROWING AGAINST THE HOME, quantified.
              *
              * This module already named the product in the strategy title and
              * carried a what/how/consider explainer — better than Aged Care had.
              * What it lacked were the two things that actually caught the owner
              * out there (2026-09-01): the debt in DOLLARS, and the line saying
              * no repayment appears in the cashflow. There are no repayments, so
              * the loan puts nothing in the figures above, and its whole cost
              * lands in the closing balance.
              */}
            {(() => {
                const er = (strategy as { equityRelease?: Record<string, unknown> }).equityRelease;
                if (!er || !(Number(er.loanAmount) > 0)) return null;
                return (
                    <EquityReleaseTerms
                        className="mb-3"
                        kind={er.kind === 'heas' ? 'heas' : 'reverseMortgage'}
                        loanAmount={Number(er.loanAmount) || 0}
                        interestRate={Number(er.interestRate) || null}
                        projectedBalance={Number(er.projectedBalance) || null}
                        years={Number(er.years) || null}
                        homeValue={Number(factFind?.home?.value) || null}
                    />
                );
            })()}
            <div className="mt-2">
                <button
                    type="button"
                    onClick={() => { setSelectedStrategyForDeepDive(strategy); setDeepDiveModal(true); }}
                    className="text-[13px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy"
                >
                    See the year-by-year figures
                </button>
            </div>
        </div>
    );

    /** The plan in words, for the card, the dialogs and the saved record. */
    const wordsFor = (row: SahDisplayStrategy | null | undefined) => sahPlanWords(row?.config as SahConfigLike, factFind, String(row?.name || ''));
    const household = householdVoice(factFind);
    const baselineFigures = analysis?.doNothingStrategy?.snapshot ? figuresFromSnapshot(analysis.doNothingStrategy.snapshot as Record<string, unknown>) : null;
    const pensionWhyWords = (() => {
        const ps = analysis?.problemStatement?.pensionOptimization as { assessableAssets?: number; assetThreshold?: number; status?: string } | undefined;
        if (!ps) return 'The Age Pension is worked out under both the assets test and the income test; the lower result is paid.';
        if (ps.status === 'full') return `${household.Names} ${household.are} on the full Age Pension, so the plans cannot raise it further.`;
        return `The Age Pension is worked out under both the assets test and the income test, and the lower result is paid. Centrelink counts ${formatCurrency(Number(ps.assessableAssets) || 0)} of ${household.possessive} assets; each $1,000 less that is counted adds about $78 a year, until the income test takes over.`;
    })();

    /*
     * A TESTED PLAN WITH A GUARANTEED INCOME (owner, 21 Oct 2026 — the guaranteed income review E4/E5, mock-up
     * screens 3 and 4). Its card shows what the family receives from it at the start, middle and end of the
     * period, against the support alone where the guaranteed income is the plan's only piece (then the support alone IS
     * the same plan without it). An income-only one cannot be chosen until the client has confirmed, for this
     * amount, that nothing comes back (IncomeOnlyBeforeChoose; the record is printed in the advice document).
     */
    const sahGuaranteedIncomeFor = (row: { config?: unknown; projections?: unknown[] }) => {
        const a = sahPlanAnnuityOf(row.config as never);
        if (!a) return null;
        const rows = (row.projections || []) as never[];
        const doNothingRows = ((analysis?.doNothingStrategy as { projections?: unknown[] } | undefined)?.projections || []) as never[];
        const held = incomeOnlyHeld(factFind, a.incomeOnly, a.amount);
        return {
            familyPanel: (
                <AnnuityFamilyPanel
                    factFind={factFind}
                    personName={sahAnnuityPersonName(factFind, a.owner)}
                    amount={a.amount}
                    kind={a.kind}
                    incomeAYear={planAnnuityIncomeOf(rows, factFind)}
                    rows={rows}
                    compareRows={a.only && doNothingRows.length ? doNothingRows : null}
                    compareNote={a.only ? SAH_COMPARE_NOTE : null}
                    horizonYears={answers.years}
                    testId="sah-annuity-family-panel"
                />
            ),
            beforeChoose: a.incomeOnly && setIncomeOnlyConfirmed
                ? <IncomeOnlyBeforeChoose factFind={factFind} amount={a.amount} onChange={setIncomeOnlyConfirmed} />
                : null,
            chooseHeld: held,
        };
    };

    /** Choosing a plan saves it as THE chosen Support at home plan (saving is choosing). */
    const choosePlan = async (row: SahDisplayStrategy, opts: { refresh?: boolean } = {}) => {
        if (!homeCareSaveContext) return;
        // Income only is held until the client has confirmed it for this amount (see sahGuaranteedIncomeFor).
        const plannedAnnuity = sahPlanAnnuityOf(row.config as never);
        if (plannedAnnuity && incomeOnlyHeld(factFind, plannedAnnuity.incomeOnly, plannedAnnuity.amount)) return;
        const words = wordsFor(row);
        const linkedAgedCareForSave = contributionContextMode === 'linked' && effectiveAgedCareIntegration;
        const built = await buildHomeCareStrategyFromSuggestionEngine({
            factFind,
            evaluatedStrategy: row,
            name: words.title,
            selectedAgedCareIntegration: linkedAgedCareForSave ? effectiveAgedCareIntegration : null,
            agedCareResidencyOverrides: linkedAgedCareForSave ? linkedResidencyOverrides : {},
            participantType: homeCareSaveContext.participantType,
            nwopCategory: homeCareSaveContext.nwopCategory,
            meansDisclosed: homeCareSaveContext.meansDisclosed,
            previousITCF: Number(homeCareSaveContext.previousITCF) || 0,
            isPartnered: homeCareSaveContext.isPartnered,
            agedCareContext: linkedAgedCareForSave ? 'linked' : 'current',
        });
        // Choosing the same plan again replaces its record rather than adding another.
        const fingerprint = JSON.stringify(effectiveServicePlan ?? {});
        const previous = getSavedHomeCareStrategies().find((s) => {
            const inp = (s.inputs || {}) as Record<string, unknown>;
            return s.source === 'suggestion_engine' && inp.strategyName === row.name && inp.servicePlanFingerprint === fingerprint;
        });
        const saved = {
            ...built,
            ...(previous ? { id: previous.id } : {}),
            // A refresh keeps the day the plan was chosen; only its figures follow the new run.
            ...(opts.refresh && previous ? { createdAt: previous.createdAt, retestedAt: new Date().toISOString() } : {}),
            displayName: words.title,
            inputs: {
                ...(built.inputs || {}),
                engineStrategyId: row.name,
                servicePlanFingerprint: fingerprint,
                // The household it was priced on, so Home can tell when that household has changed (sahChosenStale.ts).
                householdFingerprint,
                legacyYears: answers.years,
                servicePlan: effectiveServicePlan,
                // The card as chosen — the plan page shows and explains it from this (sahChosenPlan.ts).
                card: sahCardRecord({
                    plan: figuresFromSnapshot((row.snapshot || {}) as Record<string, unknown>),
                    baseline: baselineFigures,
                    words,
                    drawLines: abpDrawSentences(abpDrawChangesFromAllocation(row.assetAllocation, factFind)),
                    riskKinds: riskKindsFromSupportAtHomeType(row.config?.type, String(row.name || '')),
                    horizonYears: answers.years,
                    origin: 'engine',
                    engineConfig: row.config,
                    moved: sahMovedAmount(row.config as SahConfigLike).amount,
                }),
            },
            baseline: baselineFigures ? { legacyValue: baselineFigures.legacy, pension: baselineFigures.pension, cashFlow: baselineFigures.cashflow, contribution: baselineFigures.contribution } : null,
        } as SavedStrategy;
        persistHomeCareStrategy(saved);
        onStrategySaved?.(saved);
        onStrategySelect?.(row as unknown as Record<string, unknown>);
        setChosenName(String(row.name));
        setChosenRecord({ fingerprint, household: householdFingerprint, title: words.title });
        setChosenSaved({ saved, row });
    };

    /*
     * THE CHOSEN PLAN'S SAVED FIGURES FOLLOW ITS RE-TEST (25 Oct 2026). Home and the plan page read the
     * record saved when the plan was chosen; a plan chosen on 24 Oct read $4,379,840 there and $4,259,667 on
     * its card once the whole aged care plan was carried. A run that finds the chosen plan again, on the
     * same services, re-saves it (same record, same day chosen) where its figures moved.
     */
    useEffect(() => {
        if (!analysis) return;
        const fingerprint = JSON.stringify(effectiveServicePlan ?? {});
        if ((analysis.servicePlanFingerprint ?? fingerprint) !== fingerprint) return;
        // Only a run on the household as it is now speaks for the chosen plan (25 Oct 2026).
        if (analysis.householdFingerprint !== householdFingerprint) return;
        const saved = getSavedHomeCareStrategies().find((s) => (s as { chosen?: boolean }).chosen && s.source === 'suggestion_engine'
            && (s.inputs as { servicePlanFingerprint?: string } | undefined)?.servicePlanFingerprint === fingerprint);
        const name = (saved?.inputs as { strategyName?: unknown } | undefined)?.strategyName;
        if (!saved || name == null) return;
        // Chosen on another household: Home and the plan page already say so (sahChosenStale.ts); choosing again replaces it.
        if (sahHouseholdOf(saved) !== householdFingerprint) return;
        const row = ((analysis.allEvaluatedStrategies || analysis.strategies || []) as SahDisplayStrategy[]).find((s) => s.name === name);
        if (!row) { markChosenHomeCarePlanOutOfDate(NOT_FOUND_ON_RETEST); return; }
        const legacy = Number((row.snapshot as { legacyValue?: unknown } | undefined)?.legacyValue);
        const was = Number((saved.outputs as { legacyEstimate?: unknown } | undefined)?.legacyEstimate);
        if (!Number.isFinite(legacy) || (Math.round(legacy) === Math.round(was) && !outOfDateOf(saved))) return;
        void choosePlan(row, { refresh: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis]);

    /**
     * The chosen plan's advice document. Straight after choosing, the plan just chosen; on a later
     * visit (when that is gone) the chosen record on file and its row in this run (19 Oct 2026: the
     * banner's button did nothing after a reload).
     */
    const openAdviceDocument = () => {
        const target = chosenSaved ?? (() => {
            const saved = getSavedHomeCareStrategies().find((s) => (s as { chosen?: boolean }).chosen && s.source === 'suggestion_engine');
            const all = ((analysis?.allEvaluatedStrategies || analysis?.strategies || []) as SahDisplayStrategy[]);
            const row = chosenName ? all.find((s) => s.name === chosenName) : undefined;
            return saved && row ? { saved, row } : null;
        })();
        if (!target) return;
        void openDigitalSoaForSavedStrategy(target.saved, target.row);
    };

    // The chosen plan reads as chosen only in a run on the services it was priced on (see chosenRecord).
    const runFingerprint = analysis?.servicePlanFingerprint ?? JSON.stringify(effectiveServicePlan ?? {});
    const chosenSameServices = !!chosenName && chosenRecord?.fingerprint != null && chosenRecord.fingerprint === runFingerprint;
    // ...and on the household it was priced on (25 Oct 2026: a plan chosen with both at home is not chosen in a run
    // that carries John's aged care plan, even where a plan of the same name appears in it).
    const runHousehold = analysis?.householdFingerprint ?? householdFingerprint;
    const chosenHere = chosenSameServices && chosenRecord?.household === runHousehold ? chosenName : null;
    const chosenForOtherServices = chosenName && !chosenSameServices ? chosenRecord?.title ?? null : null;
    // Why the chosen plan is not here, when the services are the same: another household, or the run did not find it.
    const chosenOutOfDate = (() => {
        if (!chosenName || !chosenSameServices || !analysis) return null;
        const rows = (analysis.allEvaluatedStrategies || analysis.strategies || []) as SahDisplayStrategy[];
        if (chosenHere && rows.some((s) => s.name === chosenHere)) return null;
        const saved = getSavedHomeCareStrategies().find((s) => (s as { chosen?: boolean }).chosen && s.source === 'suggestion_engine');
        const reason = chosenHere ? NOT_FOUND_ON_RETEST : sahChosenOutOfDate(saved, factFind, getAllSavedStrategies());
        return reason ? { title: chosenRecord?.title ?? '', reason } : null;
    })();

    // The plan page asked for the advice document: open it once the plans are back.
    const adviceRequested = useRef(openAdviceWhenReady);
    useEffect(() => {
        if (!adviceRequested.current || !analysis || !chosenName) return;
        adviceRequested.current = false;
        openAdviceDocument();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis, chosenName]);

    // The plan page asked for the chosen plan's year-by-year figures: open them once the plans are back.
    const yearByYearRequested = useRef(openYearByYearWhenReady);
    useEffect(() => {
        if (!yearByYearRequested.current || !analysis || !chosenName) return;
        yearByYearRequested.current = false;
        const row = ((analysis.allEvaluatedStrategies || analysis.strategies || []) as SahDisplayStrategy[]).find((s) => s.name === chosenName);
        if (!row) return;
        setSelectedStrategyForDeepDive(row);
        setDeepDiveModal(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis, chosenName]);

    /** The plans side by side: the support alone, then each goal's leader once. */
    const compareRows = (() => {
        const base = analysis?.doNothingStrategy;
        const leaders = goalComparisonRows.reduce<{ strategy: SahDisplayStrategy; goals: string[] }[]>((acc, r) => {
            const hit = acc.find((x) => x.strategy.name === r.strategy.name);
            if (hit) hit.goals.push(r.goalLabel); else acc.push({ strategy: r.strategy, goals: [r.goalLabel] });
            return acc;
        }, []);
        const cell = (s: SahDisplayStrategy | null | undefined) => {
            const f = figuresFromSnapshot((s?.snapshot || {}) as Record<string, unknown>);
            return { f, moved: sahMovedAmount(s?.config as SahConfigLike).amount };
        };
        const baseCell = base ? cell(base) : null;
        const rows: ComparisonRow[] = [];
        if (base && baseCell) {
            rows.push({
                key: 'do-nothing', baseline: true,
                title: 'The support alone — the same services, nothing else changed', subtitle: 'the comparison',
                cells: {
                    bestFor: { value: null, display: '' },
                    legacy: { value: baseCell.f.legacy },
                    cf: { value: baseCell.f.cashflow, display: baseCell.f.cashflow < 0 ? `short ${formatCurrency(Math.abs(baseCell.f.cashflow))}` : formatCurrency(baseCell.f.cashflow), tone: baseCell.f.cashflow < 0 ? 'bad' : 'good' },
                    pension: { value: baseCell.f.pension },
                    contribution: { value: baseCell.f.contribution },
                    moved: { value: 0 },
                },
            });
        }
        leaders.forEach(({ strategy, goals }) => {
            const c = cell(strategy);
            const dl = baseCell ? c.f.legacy - baseCell.f.legacy : null;
            rows.push({
                key: String(strategy.name),
                chosen: chosenHere === strategy.name,
                title: wordsFor(strategy).title,
                onOpen: () => { setShowGoalLeadersModal(false); setExplainRow(strategy); },
                cells: {
                    bestFor: { value: null, display: goals.join(' · ') },
                    legacy: { value: c.f.legacy, sub: dl != null ? <span className={dl >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{dl >= 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(dl)))} vs the support alone</span> : undefined },
                    cf: { value: c.f.cashflow, display: c.f.cashflow < 0 ? `short ${formatCurrency(Math.abs(c.f.cashflow))}` : `+${formatCurrency(c.f.cashflow)}`, tone: c.f.cashflow < 0 ? 'bad' : 'good' },
                    pension: { value: c.f.pension },
                    contribution: { value: c.f.contribution },
                    moved: { value: c.moved },
                },
            });
        });
        /*
         * THE PLAN BUILT BY HAND, beside the tested ones (review, "what stays": "a self-built plan sits
         * beside the engine's plans, with the doing-nothing row"). The chosen one, else the newest, from its
         * stored card — only when it was priced on these same services, so the columns compare like with like.
         */
        const handBuilt = getSavedHomeCareStrategies()
            .filter((s) => s.source === 'manual' && (s.inputs as { card?: unknown } | undefined)?.card)
            .filter((s) => (s.inputs as { servicePlanFingerprint?: string }).servicePlanFingerprint === JSON.stringify(effectiveServicePlan ?? {}))
            .sort((a, b) => Number(Boolean((b as { chosen?: boolean }).chosen)) - Number(Boolean((a as { chosen?: boolean }).chosen)) || String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0];
        const hbCard = (handBuilt?.inputs as { card?: { plan: { legacy: number; cashflow: number; pension: number; contribution: number }; words: { title: string }; moved?: number } } | undefined)?.card;
        if (handBuilt && hbCard) {
            const dl = baseCell ? hbCard.plan.legacy - baseCell.f.legacy : null;
            rows.push({
                key: 'hand-built',
                chosen: Boolean((handBuilt as { chosen?: boolean }).chosen),
                title: hbCard.words.title,
                subtitle: 'built by you',
                cells: {
                    bestFor: { value: null, display: '' },
                    legacy: { value: hbCard.plan.legacy, sub: dl != null ? <span className={dl >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{dl >= 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(dl)))} vs the support alone</span> : undefined },
                    cf: { value: hbCard.plan.cashflow, display: hbCard.plan.cashflow < 0 ? `short ${formatCurrency(Math.abs(hbCard.plan.cashflow))}` : `+${formatCurrency(hbCard.plan.cashflow)}`, tone: hbCard.plan.cashflow < 0 ? 'bad' : 'good' },
                    pension: { value: hbCard.plan.pension },
                    contribution: { value: hbCard.plan.contribution },
                    moved: { value: Number(hbCard.moved) || 0 },
                },
            });
        }
        return rows;
    })();

    if (loading) {
        return (
            <SuggestionEngineAnalysisLoader
                title={isReoptimising ? 'Testing the ways again with your new answers...' : `Testing the ways to help ${household.names}`}
                subtitle="We're working through a large number of simulated options - this may take a minute."
                progress={progress}
                progressLabel="Testing way"
            />
        );
    }

    const setupSeen = Boolean((factFind?.goals as { sahSetupSeen?: boolean } | undefined)?.sahSetupSeen);
    const saveAnswersAndRun = () => {
        if (setFactFind) {
            setFactFind((prev) => ({
                ...prev,
                ...(hasStructures ? { structureFundingInSuggestions } : {}),
                goals: { ...(prev?.goals || {}), ...goalsFromAnswers(answers) },
            }));
        }
        onLegacyYearsChange?.(answers.years);
        setQuestionsOpen(false);
        maybeRunAnalysis(true);
    };
    const questionsNode = (idPrefix: string) => (
        <>
            <SahQuestions
                value={answers}
                onChange={setAnswers}
                factFind={factFind}
                bindingTest={bindingTest}
                suggestedReserve={suggestedReserve}
                debtTotal={clientLiabilities.reduce((s, l) => s + l.balance, 0)}
                idPrefix={idPrefix}
                guaranteedIncome={{ onGoals: writeGuaranteedIncomeGoals, investable: annuityInvestable.amount, madeOf: annuityInvestable.madeOf }}
            />
            {hasStructures && (
                <div className="border-t border-slate-100 py-4">
                    <StructureFundingInSuggestionsFields value={structureFundingInSuggestions} onChange={setStructureFundingInSuggestions} />
                </div>
            )}
            {/* Whether the aged care plan is included is step 1's question ("Is Colin moving into aged
                care?" — sahEngineContext.ts), asked once there. Here only which plan, when several are
                saved and none is chosen. */}
            {shouldOfferContributionContextSwitch && contributionContextMode === 'linked' && availableAgedCareStrategies.length > 1 && !availableAgedCareStrategies.some((s) => (s as { chosen?: boolean }).chosen) && (
                <div className="border-t border-slate-100 py-4" data-testid="sah-q-linked">
                    <p className="text-[15px] font-semibold text-slate-900">Which aged care plan is included?</p>
                    {contributionContextMode === 'linked' && availableAgedCareStrategies.length > 1 && (
                        <select value={selectedAgedCareStrategyId ? String(selectedAgedCareStrategyId) : ''} onChange={(e) => setSelectedAgedCareStrategyId(e.target.value || null)} className="mt-2 w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-[14px]">
                            <option value="">Choose the aged care plan…</option>
                            {availableAgedCareStrategies.map((s) => <option key={s.id} value={String(s.id)}>{String((s as { displayName?: string }).displayName || s.name)}</option>)}
                        </select>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div className="space-y-6">
            {showNewMoneyGate && (
                <NewMoneyProfileGate
                    onAnswer={answerNewMoneyProfile}
                    onSkip={skipNewMoneyProfile}
                    contextLine="Some of the strategies I'll compare invest money that gets freed up — for example if downsizing your home is modelled."
                />
            )}
            {showTbcGate && (
                <TransferCapGate
                    name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                    onAnswer={answerTbc}
                    onSkip={skipTbc}
                />
            )}

            {!analysis && !error && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="sah-questions-inline">
                    <h2 className="text-[22px] font-bold leading-tight tracking-tight text-slate-900">{setupSeen ? 'Ready to test the ways to help' : 'Five questions before we test the ways to help'}</h2>
                    <p className="mt-1 max-w-[72ch] text-[14px] leading-relaxed text-slate-600">
                        Our suggestions are filled in. Change any of them and the plans change with them; you can come back to these at any time under &quot;Things that change the answer&quot;.
                    </p>
                    {onBuildYourOwn && <BuildYourselfLine onClick={() => onBuildYourOwn(null)} testId="sah-build-own-top" />}
                    <div className="mt-2">{questionsNode('sahq')}</div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        {onBuildYourOwn
                            ? <button type="button" onClick={() => onBuildYourOwn(null)} className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-[15px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid="sah-setup-build-own">Build a plan yourself instead</button>
                            : <span />}
                        <Button type="button" variant="primary" size="lg" onClick={saveAnswersAndRun} disabled={isLinkedContextUnavailable} data-testid="sah-run-plans">Test the ways to help</Button>
                    </div>
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-rose-500 bg-white p-4 text-[14px] text-slate-700" role="alert">
                    <p className="font-semibold text-slate-900">{error}</p>
                    <button type="button" onClick={() => { setError(null); setQuestionsOpen(true); }} className="mt-1 font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Change the answers and try again</button>
                </div>
            )}

            {analysis && analysis.strategies && (
                <SahPlansView
                    factFind={factFind}
                    doNothing={(analysis.doNothingStrategy || null) as never}
                    recommended={recommendedOptimal}
                    strategies={(analysis.allEvaluatedStrategies || analysis.strategies) as never}
                    waysTested={analysis.totalEvaluated || tableStrategies.length}
                    horizonYears={answers.years}
                    focus={(optimisationFocus === 'all' ? 'legacy' : optimisationFocus) as GoalFocusId}
                    onFocus={(f) => setOptimisationFocus(f)}
                    chosenName={chosenHere}
                    onChoose={(row) => { void choosePlan(row as SahDisplayStrategy); }}
                    onExplain={(row) => setExplainRow(row as SahDisplayStrategy)}
                    onRisks={(row) => setRiskStrategy(row as SahDisplayStrategy)}
                    onWhy={(row, page) => setFigureFor({ row: row as SahDisplayStrategy, page })}
                    onCareUp={(row) => setCareUpRow(row as SahDisplayStrategy)}
                    renderDetail={(row) => renderStrategyDetail(row as SahDisplayStrategy)}
                    onCompare={() => setShowGoalLeadersModal(true)}
                    onAllWays={() => setShowAllStrategiesTable(true)}
                    onSettings={() => setQuestionsOpen(true)}
                    onBuildYourOwn={(row) => onBuildYourOwn?.(row as unknown as Record<string, unknown> | null)}
                    onAdvice={openAdviceDocument}
                    guaranteedIncomeFor={(row) => sahGuaranteedIncomeFor(row as never)}
                    notices={
                        <>
                            {chosenForOtherServices && (
                                <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="sah-chosen-other-services">
                                    <strong className="text-slate-900">&ldquo;{chosenForOtherServices}&rdquo; is still {household.possessive} chosen plan, priced on the services as they were then.</strong> These plans are tested on the services as they are now, so nothing here is chosen until you choose it; the earlier plan stays chosen until you do.
                                </p>
                            )}
                            {chosenOutOfDate && (
                                <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="sah-chosen-out-of-date">
                                    <strong className="text-slate-900">&ldquo;{chosenOutOfDate.title}&rdquo; is still {household.possessive} chosen plan.</strong> {chosenOutOfDate.reason.replace(/ — open it to choose again\.$/, '.')} Nothing here is chosen until you choose a plan.
                                </p>
                            )}
                            {(analysis.householdFingerprint ?? (applyLinkedAgedCareScenario ? 'before-whole-plan' : householdFingerprint)) !== householdFingerprint && (
                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-plans-stale-household">
                                    <p><strong className="text-slate-900">{applyLinkedAgedCareScenario ? 'These plans were tested before the aged care plan was carried in.' : 'These plans were tested with the aged care plan carried in.'}</strong> The figures below are for the household as it was then.</p>
                                    <Button type="button" variant="secondary" size="sm" onClick={() => runAnalysis()}>Test the plans again</Button>
                                </div>
                            )}
                            {analysis.servicePlanFingerprint !== undefined && analysis.servicePlanFingerprint !== JSON.stringify(effectiveServicePlan ?? {}) && (
                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-plans-stale">
                                    <p><strong className="text-slate-900">The services have changed since these plans were tested.</strong> The figures below are for the services as they were.</p>
                                    <Button type="button" variant="secondary" size="sm" onClick={() => runAnalysis()}>Test again with the new services</Button>
                                </div>
                            )}
                            {applyLinkedAgedCareScenario && effectiveAgedCareIntegration && (
                                <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-plans-linked">
                                    {/* The whole plan, in the client's words (24 Oct 2026; it said only "its room deposit and yearly fees"). */}
                                    These plans start from the chosen aged care plan, <strong className="text-slate-900">&ldquo;{selectedAgedCareStrategyForIntegration ? savedPlanTitle(selectedAgedCareStrategyForIntegration as never, factFind) : String((effectiveAgedCareIntegration as { displayName?: string }).displayName || effectiveAgedCareIntegration.name || 'the aged care plan')}&rdquo;</strong>, carried out in full: its deposit and everything else it does, and its yearly fees. What it uses and what is left is on the step before.
                                </p>
                            )}
                        </>
                    }
                />
            )}

            {deferredSoa && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-sm text-slate-700">
                        <span className="font-semibold text-slate-900">{deferredSoa.saved.name || 'Your plan'}</span> is saved as your plan. The advice document is ready when you want it.
                    </p>
                    <Button type="button" variant="secondary" size="sm" onClick={() => { void openDigitalSoaForSavedStrategy(deferredSoa.saved, deferredSoa.pending); }}>
                        Open the advice document
                    </Button>
                </div>
            )}

            {questionsOpen && (
                <DialogShell
                    titleId="sah-questions-heading"
                    testId="sah-questions-dialog"
                    eyebrow={`Support at home for ${household.names}`}
                    title="Things that change the answer"
                    intro="Change any answer and the plans are tested again."
                    onClose={() => setQuestionsOpen(false)}
                    size="lg"
                    footer={
                        <>
                            <Button type="button" variant="secondary" onClick={() => setQuestionsOpen(false)}>Cancel</Button>
                            <Button type="button" variant="primary" onClick={saveAnswersAndRun} disabled={isLinkedContextUnavailable}>Test the ways again</Button>
                        </>
                    }
                >
                    {questionsNode('sahqd')}
                </DialogShell>
            )}

            {showAssumptionsModal && analysis?.modellingAssumptions && (
                <DialogShell titleId="sah-assumptions-heading" title="What the tests assume" intro="The plans are modelled on these assumptions." onClose={() => setShowAssumptionsModal(false)}>
                    <ul className="list-disc space-y-2 pl-5 text-[14px] text-slate-700">
                        {assumptionsModalBullets.map((bullet, idx) => (
                            <li key={idx}>{bullet.category && bullet.category !== 'Not factored in' ? <><strong>{bullet.category}:</strong> {bullet.text}</> : bullet.text}</li>
                        ))}
                    </ul>
                </DialogShell>
            )}

            {showAllStrategiesTable && tableStrategies.length > 0 && (
                <DialogShell
                    titleId="sah-all-ways-heading"
                    testId="sah-all-ways"
                    eyebrow={`Support at home for ${household.names}`}
                    title={`All ${tableStrategies.length} ways tested`}
                    intro="Every way tested, ranked by the figure you choose. Open one to see it year by year."
                    onClose={() => setShowAllStrategiesTable(false)}
                    size="xl"
                    flush
                >
                    <div className="flex items-center gap-2 px-6 pt-4 text-[13px] text-slate-600">
                        <label htmlFor="sah-all-ways-sort">Ranked by</label>
                        <select id="sah-all-ways-sort" value={allStrategiesSortBy} onChange={(e) => setAllStrategiesSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')} className="rounded-lg border border-slate-300 bg-white px-2 py-1">
                            <option value="legacy">What is left to the family</option>
                            <option value="cashflow">Left over each year</option>
                            <option value="pension">Age Pension</option>
                        </select>
                    </div>
                    <div className="overflow-x-auto px-6 pb-6 pt-3">
                        <table className="w-full border-collapse text-[14px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-[12.5px] font-semibold text-slate-500">
                                    <th className="py-2.5 pr-3 text-left">Plan</th>
                                    <th className="px-3 py-2.5 text-right">Left to the family</th>
                                    <th className="px-3 py-2.5 text-right">Each year</th>
                                    <th className="px-3 py-2.5 text-right">Age Pension</th>
                                    <th className="py-2.5 pl-3 text-right">{household.Their} contribution</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis?.doNothingStrategy?.snapshot && (() => {
                                    const f = figuresFromSnapshot(analysis.doNothingStrategy.snapshot as Record<string, unknown>);
                                    return (
                                        <tr className="border-b border-slate-200 text-slate-500">
                                            <td className="py-3 pr-3">The support alone</td>
                                            <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(f.legacy)}</td>
                                            <td className="px-3 py-3 text-right tabular-nums">{f.cashflow < 0 ? `short ${formatCurrency(Math.abs(f.cashflow))}` : formatCurrency(f.cashflow)}</td>
                                            <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(f.pension)}</td>
                                            <td className="py-3 pl-3 text-right tabular-nums">{formatCurrency(f.contribution)}</td>
                                        </tr>
                                    );
                                })()}
                                {tableStrategies.map((s, idx) => {
                                    const f = figuresFromSnapshot((s.snapshot || {}) as Record<string, unknown>);
                                    return (
                                        <tr
                                            key={`${s.name}-${idx}`}
                                            onClick={() => { setShowAllStrategiesTable(false); setSelectedStrategyForDeepDive(s); setDeepDiveModal(true); }}
                                            className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                                        >
                                            <td className="py-3 pr-3 font-medium text-slate-900">{wordsFor(s).title}{chosenHere === s.name && <span className="ml-2 text-[12px] font-semibold text-emerald-700">✓ Chosen</span>}</td>
                                            <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(f.legacy)}</td>
                                            <td className={`px-3 py-3 text-right tabular-nums ${f.cashflow < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{f.cashflow < 0 ? `short ${formatCurrency(Math.abs(f.cashflow))}` : `+${formatCurrency(f.cashflow)}`}</td>
                                            <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(f.pension)}</td>
                                            <td className="py-3 pl-3 text-right tabular-nums">{formatCurrency(f.contribution)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </DialogShell>
            )}

            {showGoalLeadersModal && compareRows.length > 0 && (
                <DialogShell
                    titleId="sah-compare-heading"
                    testId="sah-compare"
                    eyebrow="The best plan for each goal, and the support alone"
                    title="All the plans, side by side"
                    intro={`Figures are for about ${answers.years} years, with the services priced on step 1. Open a plan to see it explained.`}
                    onClose={() => setShowGoalLeadersModal(false)}
                    size="xl"
                    footer={<button type="button" onClick={() => { setShowGoalLeadersModal(false); setShowAllStrategiesTable(true); }} className="mr-auto text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Show everything tested — all {tableStrategies.length} ways ›</button>}
                >
                    <ComparisonTable
                        testId="sah-compare-table"
                        firstHeading="Plan"
                        columns={[
                            { key: 'bestFor', label: 'Best for', text: true },
                            { key: 'legacy', label: 'Left to the family', best: 'max', headline: true },
                            { key: 'cf', label: 'Each year', best: 'max' },
                            { key: 'pension', label: 'Age Pension a year', best: 'max' },
                            { key: 'contribution', label: `${household.Their} contribution a year`, best: 'min' },
                            { key: 'moved', label: 'Money moved', bar: 'amber' },
                        ]}
                        rows={compareRows}
                    />
                </DialogShell>
            )}

            {explainRow && (() => {
                const words = wordsFor(explainRow);
                const plan = figuresFromSnapshot((explainRow.snapshot || {}) as Record<string, unknown>);
                const doSentence = `${words.title}${words.sub ? ` — ${words.sub.replace(/^Paid from /, 'paid from ').replace(/\.$/, '')}` : ''}.`;
                // Where a short year is drawn from, account by account, off this plan's own rows (as aged care's cards).
                const storyRows = ((explainRow as { projections?: unknown[] }).projections || []) as unknown[];
                const story = summariseFunding(storyRows as never).hasDeficit
                    ? buildMoneyStory({ rows: storyRows as never, factFind, runway: planRunwayCopy(storyRows as never, storyRows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: answers.years, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the plan' })
                    : null;
                return (
                    <PlanExplanationModal
                        eyebrow="Explain this"
                        title={words.title}
                        intro={`Each line says what ${household.they} do, and what that does to the numbers. ${SAH_BENCH.sentence}`}
                        onClose={() => setExplainRow(null)}
                        footer={<Button type="button" variant="secondary" onClick={() => { const row = explainRow; setExplainRow(null); setSelectedStrategyForDeepDive(row); setDeepDiveModal(true); }}>See the year-by-year figures</Button>}
                    >
                        <SahCauseEffect doSentence={doSentence} treatment={words.treatment} plan={plan} baseline={baselineFigures} horizonYears={answers.years} possessive={household.their} they={household.they} drawLines={abpDrawSentences(abpDrawChangesFromAllocation(explainRow.assetAllocation, factFind))} moneyStory={story ? <MoneyStoryBlock story={story} testId="sah-money-story" /> : null} leverKinds={sahLeverKinds(explainRow.config as SahConfigLike)} />
                        {(() => {
                            // The debts this plan keeps, and what the same plan with them paid off did (sahDebtTwinOf).
                            const twin = sahDebtTwinOf(explainRow, (analysis?.allEvaluatedStrategies || analysis?.strategies || []) as SahDisplayStrategy[]);
                            const twinFigures = twin ? figuresFromSnapshot((twin.snapshot || {}) as Record<string, unknown>) : null;
                            return (
                                <DebtsInPlanNote
                                    factFind={factFind}
                                    clearedAmount={sahDebtClearedBy(explainRow.config as SahConfigLike)}
                                    plan={{ estate: plan.legacy, cashflow: plan.cashflow, pension: plan.pension }}
                                    twin={twinFigures ? { estate: twinFigures.legacy, cashflow: twinFigures.cashflow, pension: twinFigures.pension } : null}
                                    years={answers.years}
                                    context="tested"
                                />
                            );
                        })()}
                    </PlanExplanationModal>
                );
            })()}

            {figureFor && (
                <SahFigurePopover
                    planTitle={wordsFor(figureFor.row).title}
                    plan={figuresFromSnapshot((figureFor.row.snapshot || {}) as Record<string, unknown>)}
                    baseline={baselineFigures}
                    initialPage={figureFor.page}
                    possessive={household.their}
                    they={household.they}
                    them={household.them}
                    pensionWhy={pensionWhyWords}
                    onYearByYear={() => { setSelectedStrategyForDeepDive(figureFor.row); setDeepDiveModal(true); }}
                    onClose={() => setFigureFor(null)}
                />
            )}

            {careUpRow && (
                <SahCareUpDialog
                    factFind={factFind}
                    setFactFind={setFactFind}
                    servicePlan={effectiveServicePlan as never}
                    years={answers.years}
                    supportStartDate={supportStartDate}
                    savedStrategies={availableAgedCareStrategies}
                    planTitle={wordsFor(careUpRow).title}
                    strategyConfig={careUpRow.config}
                    onClose={() => setCareUpRow(null)}
                />
            )}

            {riskStrategy && (
                <StrategyRisksPanel
                    strategyName={wordsFor(riskStrategy).title}
                    voice={household}
                    onClose={() => setRiskStrategy(null)}
                    risks={buildStrategyRisks(riskKindsFromSupportAtHomeType(riskStrategy.config?.type, String(riskStrategy.name || '')))}
                />
            )}

            {deepDiveModal && selectedStrategyForDeepDive && (
                <SupportAtHomeSuggestionEngineDeepDive
                    strategy={selectedStrategyForDeepDive as unknown as SupportAtHomeSuggestionEngineDeepDiveProps['strategy']}
                    factFind={factFind}
                    onClose={() => setDeepDiveModal(false)}
                    estimatedServicePlanNote={
                        analysis?.baseline?.servicePlanCosts?.isEstimated
                            ? analysis.baseline.servicePlanCosts.estimatedDescription
                            : servicePlanCosts.isEstimated
                                ? servicePlanCosts.estimatedDescription
                                : null
                    }
                />
            )}
        </div>
    );
};

export default SupportAtHomeSuggestionEnginePanel;
